import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import {
  Loader2,
  AlertCircle,
  Code,
  Eye,
  Bot,
  Maximize2,
  Minimize2,
  ExternalLink,
  Palette,
  RefreshCw,
  Clock,
  PanelLeft,
  ChevronRight,
  ChevronDown,
  X,
  MoreHorizontal,
  Download,
  Presentation,
} from "lucide-react";
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  DragOverlay,
} from "@dnd-kit/core";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Tabs, TabsContent } from "@/components/ui/tabs";

import { useVisualEditorHistoryStore } from "@/stores/visualEditorHistoryStore";
import { useThemeHistoryStore } from "@/stores/themeHistoryStore";
import { useAgentInputStore } from "@/stores/agent-input-store";
import type { PageInfo } from "@/types/pages.types";
import { getAllPendingChanges } from "@/lib/utils/visual-editor-history-utils";
import {
  AgentModeContainer,
  AgentModeContainerHandle,
} from "@/components/agent/AgentModeContainer";
import { ChatMode } from "@/components/agent/AgentModeDropdown";
import { ModelType, getDefaultModel, sanitizeModel } from "@/components/agent/ModelDropdown";
import { useCodexEnabled } from "@/hooks/use-codex-enabled";
import IframeDropZone from "@/components/visual-editor/IframeDropZone";
import VisualEditor, {
  type VisualEditorTab,
} from "@/components/visual-editor/VisualEditor";
import { ResponsiveIframeViewer } from "./ResponsiveIframeViewer";
import ProjectConnectionsModal from "./ProjectConnectionsModal";
import { PublishDropdown } from "./PublishDropdown";
import ProjectSettings from "./ProjectSettings";
import { CommitHistory } from "./CommitHistory";
import { CommitHistorySheet } from "./CommitHistorySheet";
import { ProjectSettingsSheet } from "./ProjectSettingsSheet";
import { ProjectSettingsDrawerSheet } from "./ProjectSettingsDrawerSheet";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { cn } from "@/lib/utils";
import { Hint } from "../ui/hint";
import {
  normalizeDropPosition,
  parseSourceLocation,
} from "@/lib/visual-editor/helper";
import type { ElementData } from "@/types/preview-message.types";
import {
  previewMessage,
  PreviewMessageType,
} from "@/lib/visual-editor/post-message";
import { ViewportSize, ViewportSizeType } from "@/types/viewport-device.types";
import { StructureChangeType } from "@/types/visual-editor-history.types";
import { useResponsive } from "@/hooks/useResponsive";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/theme-context";
import { claudeAgentApi } from "@/lib/api/claude-agent";
import { workspacesApi } from "@/lib/api/workspaces";
import { projectsApi } from "@/lib/api/projects";
import { devContainerAPI } from "@/lib/dev-containers/api";
import { getActiveWorkspaceId } from "@/stores/workspace-store";
import { MobileTabSwitcher } from "../ui/mobile-tab-switcher";
import { TABS } from "@/constants/agent-chat.constants";
import { GOOGLE_FONTS } from "@optidev/shared-types";
import { ViewMode, BuilderData } from "@/types/power-editor.types";
import type { ThemeContent } from "@/types/theme.types";
import { ThemesListPanel } from "./themes";
import ImageGeneratePanel from "./image-generate-panel";
import { MediaLibraryPanel } from "./media-library-panel";
import { OptisignsAssetsPanel } from "./optisigns-assets-panel";
import { FloatingToolbar } from "./floating-toolbar";
import { IconPickerPopup } from "@/components/visual-editor/components/icon-picker-popup";
import { MediaPickerPopup } from "@/components/visual-editor/components/media-picker-popup";
import { useComponentDragDrop } from "./use-component-drag-drop";
import { SlidesPanel } from "@/components/slides/slides-panel";
import { SlideNotesEditor } from "@/components/slides/slide-notes-editor";
import { ScaledSlideIframe } from "@/components/slides/scaled-slide-iframe";
import { buildPresenterUrl } from "@/routes/dashboard/editor/PresenterRoute";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useSlideStore } from "@/stores/slide-store";
import { SlideAnnotationLayer } from "@/components/slides/slide-annotation-layer";
import { exportToPptx } from "@/lib/slides/pptx-export";
import { exportToPdf } from "@/lib/slides/pdf-export";
import {
  getPendingJob,
  resumePendingExport,
} from "@/lib/slides/export-job-tracker";
import { fetchSlideManifest } from "@/lib/slides/slide-manifest";
import { setEditorContext } from "@/lib/socket/agentEventHandlers";

/**
 * Data-bound elements (.map() rendered) are protected from delete, duplicate,
 * and drag — only editable via AI. Checks `bind` and `data-optidev-dynamic`.
 */
function isDataBoundElement(el: ElementData | null | undefined): boolean {
  if (!el) return false;
  return !!(el.bind || el.attributes?.["data-optidev-dynamic"]);
}

/**
 * Map a dev-container URL's service-suffix back to the service name the
 * access-url API expects. Returns undefined for the bare IDE subdomain
 * (no suffix → port 8080 / VS Code).
 *
 *   warm-abc-vite.dev-sandbox1.optiedge.cc → "vite"
 *   user-abc-next.dev.optidev.ai           → "next"
 *   user-abc.dev.optidev.ai                → undefined
 */
function extractServiceSuffix(url: string | undefined): string | undefined {
  if (!url) return undefined;
  try {
    const hostname = new URL(url).hostname;
    const match = hostname.match(/^(?:user|warm)-[a-zA-Z0-9]+-([a-z]+)\./);
    return match?.[1];
  } catch {
    return undefined;
  }
}

/**
 * Open a dev-container URL in a new tab after minting a fresh ?tkn=. Required
 * because the iframe context's partitioned cookie (CHIPS) isn't visible when
 * the same origin becomes a top-level page — the new tab needs to bootstrap
 * its own partition.
 *
 * Opens about:blank synchronously to preserve the click's user activation
 * (popup-blocker bypass), then navigates the new window once the URL is ready.
 */
async function openWithFreshToken(
  fallbackUrl: string | undefined,
  mintUrl: () => Promise<{ url: string }>,
): Promise<void> {
  const newWin = window.open("about:blank", "_blank");
  try {
    const { url } = await mintUrl();
    if (newWin) {
      newWin.location.href = url;
    } else {
      window.open(url, "_blank");
    }
  } catch (err) {
    console.error("[IntegratedEditor] Failed to mint access URL:", err);
    if (fallbackUrl && newWin) {
      newWin.location.href = fallbackUrl;
    } else if (fallbackUrl) {
      window.open(fallbackUrl, "_blank");
    } else if (newWin) {
      newWin.close();
    }
  }
}

// Constants
const TRUSTED_DOMAINS = [
  ".optidev.com",
  ".optidev.ai",
  ".optisigns.com",
  ".kazebuilder.org",
  ".optidev.cc",
  ".optiedge.cc",
  ".alphakaze.com",
  ".danlingjiang.com",
] as const;
const LOCAL_PREVIEW_URLS = [
  "http://localhost:5173",
  "http://localhost:3005",
] as const;
const TOKEN_CLEANUP_MAX_ATTEMPTS = 20;
const TOKEN_CLEANUP_INTERVAL = 500;
const SPLIT_POSITION_LIMITS = { min: 20, max: 80 } as const;
// Left sidebar (chat/visual/etc) vs right preview split.
// Full 0-100 range so dragging the divider to an edge fully collapses that side
// (replaces the old collapse/expand chevron buttons).
const LEFT_PANEL_WIDTH_LIMITS = { min: 0, max: 100 } as const;
const LEFT_PANEL_WIDTH_DEFAULT = 29;
// Magnet snap: dragging a side below this width (%) snaps it fully closed (0 / 100).
const LEFT_PANEL_SNAP_THRESHOLD = 15;

// Below this header width (px) the Visual tabs collapse into a dropdown
const VISUAL_TABS_COMPACT_WIDTH = 380;

// Left sidebar panel wrapper component (29% width, or full width when fullscreen)
interface LeftSidebarPanelProps {
  children: React.ReactNode;
  isSidebarHidden: boolean;
  isMobile: boolean;
  isFullscreenChat?: boolean;
  variant?: "background" | "card"; // Agent uses background, others use card
  widthPercent?: number; // Resizable width (% of container), defaults to 29
}

const LeftSidebarPanel: React.FC<LeftSidebarPanelProps> = ({
  children,
  isSidebarHidden,
  isMobile,
  isFullscreenChat = false,
  variant = "card",
  widthPercent = LEFT_PANEL_WIDTH_DEFAULT,
}) => {
  // Don't render if sidebar is hidden or on mobile (mobile uses Tabs layout)
  if (isSidebarHidden || isMobile) return null;

  // While the panel is being dragged narrower than the snap threshold (but not
  // yet fully collapsed) the inner UI gets squished and looks broken. Cover it
  // with a plain overlay so only a clean blank panel shows until the drag
  // resolves to either open or fully collapsed (0).
  const showCollapsingOverlay =
    !isFullscreenChat &&
    widthPercent > 0 &&
    widthPercent < LEFT_PANEL_SNAP_THRESHOLD;

  return (
    <div
      className={cn(
        "relative flex flex-col border-r border-border overflow-hidden min-h-0",
        variant === "background" ? "bg-background" : "bg-card",
        isFullscreenChat ? "w-full border-r-0" : "shrink-0",
      )}
      style={isFullscreenChat ? undefined : { width: `${widthPercent}%` }}
    >
      {children}
      {showCollapsingOverlay && (
        <div
          className={cn(
            "absolute inset-0 z-50 pointer-events-none",
            variant === "background" ? "bg-background" : "bg-card",
          )}
        />
      )}
    </div>
  );
};

const VISUAL_TABS: readonly VisualEditorTab[] = [
  "insert",
  "property",
  "layers",
  "pages",
];

interface VisualTabsControlProps {
  activeTab: VisualEditorTab;
  onChange: (tab: VisualEditorTab) => void;
  /** When true, render a compact dropdown instead of the full tab row */
  compact: boolean;
}

// Visual editor sub-tabs: a tab row when there's room, a dropdown when the
// (resizable) panel is too narrow to fit all the labels.
const VisualTabsControl: React.FC<VisualTabsControlProps> = ({
  activeTab,
  onChange,
  compact,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPos, setMenuPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Menu is portaled to body so the panel's overflow-hidden / the preview iframe
  // can't clip it; position it just below the trigger.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setMenuPos({
        top: rect.bottom + 6,
        left: rect.left,
        width: Math.max(rect.width, 160),
      });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [isOpen]);

  if (!compact) {
    return (
      <div className="flex-1 min-w-0 flex items-center justify-center gap-1">
        {VISUAL_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => onChange(tab)}
            className={cn(
              "flex-shrink-0 px-2 py-1 text-xs font-medium transition-colors capitalize border-b-2",
              activeTab === tab
                ? "text-foreground border-purple-600"
                : "text-muted-foreground border-transparent hover:text-foreground hover:border-border",
            )}
          >
            {tab}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 flex justify-center">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1.5 h-7 pl-2.5 pr-1.5 rounded-md max-w-full",
          "border border-border bg-background dark:bg-muted/40 hover:bg-muted/80 hover:border-border",
          "text-foreground text-xs font-medium capitalize transition-colors",
          isOpen && "bg-muted/80",
        )}
      >
        <span className="truncate">{activeTab}</span>
        <ChevronDown
          className={cn(
            "w-3 h-3 text-muted-foreground transition-transform shrink-0",
            isOpen && "rotate-180",
          )}
        />
      </button>
      {isOpen &&
        menuPos &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
            className="bg-popover rounded-lg shadow-lg border border-border z-[9999] p-1"
          >
            {VISUAL_TABS.map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => {
                  onChange(tab);
                  setIsOpen(false);
                }}
                className={cn(
                  "w-full text-left px-2.5 py-1.5 rounded-md text-sm capitalize transition-colors",
                  activeTab === tab
                    ? "bg-primary/10 text-primary dark:bg-primary/20"
                    : "text-foreground hover:bg-foreground/[0.04] dark:hover:bg-foreground/[0.06]",
                )}
              >
                {tab}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

interface IntegratedEditorWithAgentProps {
  workspaceUrl: string;
  actualWorkspaceUrl?: string; // The actual production URL for opening in new window
  devContainerId: string;
  projectId?: string;
  workspaceId?: string;
  previewUrl: string;
  projectName: string;
  isLoading?: boolean;
  isPreviewLoading?: boolean;
  isContainerReady?: boolean; // Whether the dev container is ready to accept commands
  error?: string;
  isDevelopment?: boolean;
  previewPort?: string;
  viewMode?: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  initialSection?: string; // Initial settings section from URL params
  onSettingsSectionChange?: (section: string) => void; // Callback when settings section changes
  onReloadPreview?: () => void;
  onOpenPreviewInNewWindow?: () => void;
  onExpandPreview?: () => void;
  isSidebarHidden?: boolean;
  isFullscreenChat?: boolean;
  onFullscreenChatChange?: (isFullscreen: boolean) => void;
  showPreviewInAgentView?: boolean;
  onShowPreviewInAgentViewChange?: (showPreview: boolean) => void;
  onVisualEditorRef?: (ref: any) => void; // Callback to expose visual editor ref
  onCommitStateChange?: (state: "idle" | "generating" | "creating") => void; // Callback for commit state changes
  isResponsiveMode?: boolean;
  onResponsiveModeChange?: (mode: boolean) => void;
  viewportSize?: ViewportSizeType;
  onViewportChange?: (size: ViewportSizeType) => void; // Callback when viewport size changes
  isCustomViewport?: boolean;
  customWidth?: number;
  customHeight?: number;
  onCustomViewportChange?: (width: number, height: number) => void; // Callback when custom viewport dimensions change
  onViewportResize?: (width: number, height: number) => void; // Callback when viewport is resized
  onTransformRef?: (ref: any) => void; // Callback to expose transform controls
  onPreviewIframeRef?: (ref: React.RefObject<HTMLIFrameElement>) => void; // Callback to expose preview iframe ref
  onPathChange?: (path: string) => void; // Callback when route changes in iframe
  onPreviewLoaded?: () => void; // Callback when preview iframe loads successfully
  isShowingStaticPreview?: boolean; // True when showing static preview URL during resume
  fromApp?: boolean; // True when navigating from the app library after remix
  builderData?: BuilderData | null; // Data from builder flow
  onBuilderDataProcessed?: () => void; // Callback when builder data is processed (prevents duplicate sends)
  sessionHeaderActions?: React.ReactNode; // DEPRECATED - use left/right actions instead
  sessionHeaderLeftActions?: React.ReactNode; // Left actions (mode toggle: agent/visual)
  sessionHeaderRightActions?: React.ReactNode; // Right actions (code/preview, fullscreen)
  onAgentStreamingChange?: (isStreaming: boolean) => void; // Callback when agent streaming state changes
  visualEditorFloatingControls?: React.ReactNode; // Floating controls for visual editor (undo/redo/apply/discard)
  activeMenuItem?: string | null; // Active menu item from header dropdown
  onActiveMenuItemChange?: (item: string | null) => void; // Callback to change active menu item
  // Viewport navigation props
  currentPath?: string;
  availablePaths?: PageInfo[];
  showDeviceDropdown?: boolean;
  showPathDropdown?: boolean;
  onPathSelect?: (path: string) => void;
  onToggleDeviceDropdown?: () => void;
  onTogglePathDropdown?: () => void;
  showMobileTabSwitcher?: boolean;
  onMobileActiveTabChange?: (tab: "chat" | "preview" | "visual") => void;
  onHeaderVisibilityChange?: (visible: boolean) => void; // Callback to control header visibility on scroll
  onStreamComplete?: () => void; // Callback when agent stream completes (for refreshing routes, etc.)
  onConnectionLinked?: () => void; // Callback when a connection is linked to the project via HITL
  onAgentSendPrompt?: (sendFn: ((text: string) => void) | null) => void; // Callback to expose agent sendPrompt function to parent
  onMobileSettingsOpener?: (
    openFn:
      | ((
          section?: "info" | "deploy" | "optidev-cloud" | "agent-settings" | "reports",
        ) => void)
      | null,
  ) => void; // Callback to expose mobile settings opener to parent
}

export default function IntegratedEditorWithAgent({
  workspaceUrl,
  actualWorkspaceUrl,
  devContainerId,
  projectId,
  workspaceId,
  previewUrl,
  projectName,
  isLoading = false,
  isPreviewLoading = false,
  isContainerReady = true,
  error,
  isDevelopment = false,
  viewMode: externalViewMode,
  onViewModeChange: externalOnViewModeChange,
  initialSection,
  onSettingsSectionChange,
  onReloadPreview: externalOnReloadPreview,
  onOpenPreviewInNewWindow: externalOnOpenPreviewInNewWindow,
  onExpandPreview: externalOnExpandPreview,
  isSidebarHidden: externalIsSidebarHidden,
  isFullscreenChat = false,
  onFullscreenChatChange,
  showPreviewInAgentView: externalShowPreviewInAgentView,
  onShowPreviewInAgentViewChange: externalOnShowPreviewInAgentViewChange,
  onVisualEditorRef,
  onCommitStateChange,
  isResponsiveMode: externalIsResponsiveMode,
  onResponsiveModeChange: externalOnResponsiveModeChange,
  viewportSize,
  onViewportChange,
  isCustomViewport = false,
  customWidth = 1024,
  customHeight = 768,
  onCustomViewportChange,
  onViewportResize,
  onTransformRef,
  onPreviewIframeRef,
  onPathChange,
  onPreviewLoaded,
  isShowingStaticPreview = false,
  fromApp = false,
  builderData,
  onBuilderDataProcessed,
  sessionHeaderActions,
  sessionHeaderLeftActions,
  sessionHeaderRightActions,
  onAgentStreamingChange,
  visualEditorFloatingControls,
  activeMenuItem,
  onActiveMenuItemChange,
  // Viewport navigation props
  currentPath,
  availablePaths,
  showDeviceDropdown,
  showPathDropdown,
  onPathSelect,
  onToggleDeviceDropdown,
  onTogglePathDropdown,
  showMobileTabSwitcher = true,
  onMobileActiveTabChange,
  onHeaderVisibilityChange,
  onStreamComplete,
  onConnectionLinked,
  onAgentSendPrompt,
  onMobileSettingsOpener,
}: IntegratedEditorWithAgentProps) {
  const [ideLoaded, setIdeLoaded] = useState(false);
  const [previewLoaded, setPreviewLoaded] = useState(false);
  const [splitPosition, setSplitPosition] = useState(50); // Percentage
  const [isResizing, setIsResizing] = useState(false);
  // Resizable left sidebar (chat/visual/etc) vs right preview.
  // Resets to default on each load (not persisted).
  const [leftPanelWidth, setLeftPanelWidth] =
    useState<number>(LEFT_PANEL_WIDTH_DEFAULT);
  const [isResizingLeft, setIsResizingLeft] = useState(false);
  // Visual editor tab row width (resizable panel) -> collapse tabs to icons when narrow
  const visualHeaderRef = useRef<HTMLDivElement>(null);
  const [visualHeaderWidth, setVisualHeaderWidth] = useState(0);
  const [isFullscreenIde, setIsFullscreenIde] = useState(false);
  const [isFullscreenPreview, setIsFullscreenPreview] = useState(false);

  const [internalViewMode, setInternalViewMode] = useState<ViewMode>("agent"); // Default to agent view

  // Use external viewMode if provided, otherwise use internal
  const viewMode = externalViewMode || internalViewMode;
  const setViewMode = externalOnViewModeChange || setInternalViewMode;
  const [selectedElement, setSelectedElement] = useState<ElementData | null>(
    null,
  );
  const [selectedElements, setSelectedElements] = useState<ElementData[]>([]);
  const [isSelectionEnabled, setIsSelectionEnabled] = useState(true);
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);

  // Floating toolbar state
  const [toolbarBounds, setToolbarBounds] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    top: number;
    bottom: number;
    left: number;
    right: number;
  } | null>(null);
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [internalIsResponsiveMode, setInternalIsResponsiveMode] =
    useState(true); // Toggle responsive mode
  const isResponsiveMode =
    externalIsResponsiveMode !== undefined
      ? externalIsResponsiveMode
      : internalIsResponsiveMode;
  const setIsResponsiveMode = externalOnResponsiveModeChange
    ? externalOnResponsiveModeChange
    : setInternalIsResponsiveMode;
  const [isPanningDisabled, setIsPanningDisabled] = useState(false); // Disable panning when resizing
  const [transformKey, setTransformKey] = useState(0); // Force re-render of TransformWrapper
  const [internalIsSidebarHidden] = useState(false);
  const [showFloatingControls, setShowFloatingControls] = useState(true); // Toggle floating controls visibility
  // Sidebar visibility is now driven by the resizable divider (drag to an edge to
  // fully collapse a side). The external prop is still honored if a parent supplies it.
  const isSidebarHidden =
    externalIsSidebarHidden !== undefined
      ? externalIsSidebarHidden
      : internalIsSidebarHidden;
  const [isStreaming] = useState(false);
  const [visualEditorTab, setVisualEditorTab] =
    useState<VisualEditorTab>("property");

  // Callback to notify parent of commit state changes
  const setCommitState = (state: "idle" | "generating" | "creating") => {
    if (onCommitStateChange) {
      onCommitStateChange(state);
    }
  };
  const { isMobile: isMobileDevice } = useResponsive();
  // Only actual mobile devices use mobile layout (tabs at bottom)
  // Tablets use desktop split layout (agent 29% + preview 71% side by side)
  const isMobile = isMobileDevice;
  const { canEditFiles } = usePermissions();
  const { getIdToken } = useAuth();
  const { resolvedTheme } = useTheme();
  const isSlideProject = useSlideStore((s) => s.isSlideProject);
  const isExporting = useSlideStore((s) => s.isExporting);
  const isSlidesCollapsed = useSlideStore((s) => s.isSidebarCollapsed);
  const setSlidesCollapsed = useSlideStore((s) => s.setSidebarCollapsed);
  // For the SlideNotesEditor mounted as `bottomSlot` on the preview iframe.
  const slidesForNotes = useSlideStore((s) => s.slides);
  const currentSlideIndexForNotes = useSlideStore((s) => s.currentSlideIndex);

  // In-page Present overlay (the editor tab's audience view). Not the
  // Fullscreen API — covers the editor's chrome via `fixed inset-0 z-50`
  // but stays inside the browser window so the user can switch tabs.
  // Toggled by the "Presenter view" dropdown item.
  // `presentInitialUrl` is captured ONCE when entering present mode so the
  // iframe src is stable; subsequent navigation goes through postMessage
  // and the template's SlideRenderer handles it internally (no reload).
  const [isPresentMode, setIsPresentMode] = useState(false);
  const [presentInitialUrl, setPresentInitialUrl] = useState<string>("");
  const presentOverlayIframeRef = useRef<HTMLIFrameElement | null>(null);
  // When true on Present-mode-open, also call requestFullscreen() on the
  // overlay root so the user gets browser fullscreen UX (hide chrome,
  // Esc-to-exit). False = in-page overlay only (chevron → Presenter view
  // path). The overlay root is the same DOM either way, so the annotation
  // layer keeps working in both modes — see overlay portal at bottom of
  // render.
  const [presentRequestFullscreen, setPresentRequestFullscreen] =
    useState(false);
  const presentOverlayRootRef = useRef<HTMLDivElement | null>(null);
  // Read the deck's aspect ratio so the overlay can letterbox the iframe.
  // This is the defense layer for existing projects whose bundled
  // SlideRenderer.tsx predates the template's own letterbox.
  const presentAspectRatio = useSlideStore(
    (s) => s.presentationConfig.aspectRatio,
  );

  // Listen for the presenter window's BroadcastChannel messages while this
  // is a slides project. Forwards `slide-navigate` into the editor's
  // present-mode overlay iframe (the audience) and exits present mode on
  // `end-presentation`. Both windows are Kaze origin so BroadcastChannel
  // works directly.
  useEffect(() => {
    if (!isSlideProject || !projectId) return;
    const bc = new BroadcastChannel(`optidev-slide-sync:${projectId}`);
    bc.onmessage = (e) => {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.type === "slide-navigate") {
        // Forward to the overlay if active, otherwise to the inline editor
        // iframe so the editor's own preview also stays in sync.
        const targetIframe =
          presentOverlayIframeRef.current ?? previewIframeRef.current;
        targetIframe?.contentWindow?.postMessage(
          { type: "slide-navigate", slideIndex: e.data.slideIndex },
          "*",
        );
        useSlideStore.getState().setCurrentSlide(e.data.slideIndex);
      } else if (e.data.type === "presentation-config-update") {
        // Live presenter control (▶/⏸ autoplay, etc.) — forward into the
        // active audience iframe and mirror into the Kaze store so any
        // in-editor UI (slide-panel gear popover indicator, etc.) reflects
        // the live state. We do NOT call updateSlidesConfig — the gear
        // popover's debouncedSave remains the only writer to slides.json.
        const targetIframe =
          presentOverlayIframeRef.current ?? previewIframeRef.current;
        targetIframe?.contentWindow?.postMessage(e.data, "*");
        if (e.data.config && typeof e.data.config === "object") {
          useSlideStore.getState().setPresentationConfig(e.data.config);
        }
      } else if (e.data.type === "end-presentation") {
        setIsPresentMode(false);
      }
      // Annotation messages are owned by useSlideAnnotations below
      // (its own BC channel on the same name receives in parallel).
    };
    return () => bc.close();
  }, [isSlideProject, projectId]);

  // Annotation feature lives entirely inside <SlideAnnotationLayer />
  // mounted in the Present portal below. Lifecycle: passing
  // active={isPresentMode} makes the layer call clearAll() on End
  // Presentation (Canva pattern).

  // Reset the fullscreen-request flag when leaving Present mode so the
  // next chevron → Presenter view open doesn't inherit it. (Annotation
  // clearAll is owned by SlideAnnotationLayer's `active` prop.)
  useEffect(() => {
    if (isPresentMode) return;
    setPresentRequestFullscreen(false);
  }, [isPresentMode]);

  // Browser-fullscreen bridge for the LEFT "Present" button. When Present
  // mode opens with the fullscreen flag set, request fullscreen on the
  // overlay root (NOT on the iframe — the iframe alone wouldn't include
  // the annotation overlay as a descendant). Pairs the lifecycles so
  // pressing Esc to exit fullscreen also exits Present mode.
  useEffect(() => {
    if (!isPresentMode || !presentRequestFullscreen) return;
    const root = presentOverlayRootRef.current;
    if (!root) return;
    let entered = false;
    const onFSChange = () => {
      // If fullscreen was entered then exited (user pressed Esc), tear
      // down Present mode too. We don't fire on the initial transition
      // INTO fullscreen because entered flips true before this event.
      if (entered && !document.fullscreenElement) {
        setIsPresentMode(false);
        setPresentRequestFullscreen(false);
      }
    };
    document.addEventListener("fullscreenchange", onFSChange);
    root
      .requestFullscreen()
      .then(() => {
        entered = true;
      })
      .catch(() => {
        // Browser refused (permissions-policy, user gesture stale, etc.)
        // — leave the in-page overlay running so the user still has a
        // working Present mode + annotation, just without browser
        // fullscreen. Reset the flag for next open.
        setPresentRequestFullscreen(false);
      });
    return () => {
      document.removeEventListener("fullscreenchange", onFSChange);
      // Best-effort: if we still hold fullscreen on this root when the
      // effect tears down (e.g. user clicked the X close in the overlay),
      // exit fullscreen too.
      if (document.fullscreenElement === root) {
        document.exitFullscreen().catch(() => {});
      }
    };
  }, [isPresentMode, presentRequestFullscreen]);

  // Escape key exits present-mode overlay. Two paths:
  //   1. Escape pressed while the Kaze parent has focus — caught here.
  //   2. Escape pressed inside the iframe (cross-origin, keyboard events
  //      don't bubble) — the template's SlideRenderer forwards via
  //      postMessage `{ type: "presentation-exit-request" }`, caught
  //      below.
  useEffect(() => {
    if (!isPresentMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setIsPresentMode(false);
      }
    };
    const onMessage = (e: MessageEvent) => {
      if (
        e.data &&
        typeof e.data === "object" &&
        e.data.type === "presentation-exit-request"
      ) {
        setIsPresentMode(false);
      }
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("message", onMessage);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("message", onMessage);
    };
  }, [isPresentMode]);

  const buildPresentSlideUrl = useCallback(
    (slideIndex: number): string => {
      if (!previewUrl) return "";
      // `autoplay=0` disables the template's own autoplay engine inside
      // the in-page Present overlay iframe — Kaze (via PresenterDashboard)
      // drives autoplay through slide-navigate postMessages so pause is
      // reliable. Without this, the template's internal `isAutoPlaying`
      // state (initialized at mount from slides.json) stays running
      // independently of the presenter's ▶/⏸ control.
      try {
        const u = new URL(previewUrl);
        u.searchParams.set("slide", String(slideIndex));
        u.searchParams.set("presenter", "1");
        u.searchParams.set("autoplay", "0");
        return u.toString();
      } catch {
        const sep = previewUrl.includes("?") ? "&" : "?";
        return `${previewUrl}${sep}slide=${slideIndex}&presenter=1&autoplay=0`;
      }
    },
    [previewUrl],
  );
  const [mobileActiveTab, setMobileActiveTab] = useState<
    "chat" | "preview" | "visual"
  >("chat"); // Mobile bottom tab state
  const [showThemesList, setShowThemesList] = useState(false); // Track if showing themes list in design panel
  const [themeToEdit, setThemeToEdit] = useState<ThemeContent | null>(null); // Theme to import into editor
  const [mobileSubView, setMobileSubView] = useState<"agent" | "preview">(
    "agent",
  ); // Internal chat sub-view state
  const [showProjectSettingsSheet, setShowProjectSettingsSheet] =
    useState(false); // Project settings menu sheet state
  const [showProjectSettingsDrawer, setShowProjectSettingsDrawer] =
    useState(false); // Project settings drawer state
  const [projectSettingsSection, setProjectSettingsSection] = useState<
    "info" | "deploy" | "optidev-cloud" | "agent-settings" | "reports"
  >("info"); // Current section in drawer
  const [isMobileTabSwitcherVisible, setIsMobileTabSwitcherVisible] =
    useState(true); // Controls mobile tab switcher visibility based on scroll

  // Agent mode and model state
  const [agentMode, setAgentMode] = useState<ChatMode>("execute");
  const [agentModel, _setAgentModel] = useState<ModelType>(getDefaultModel);
  const { enabled: codexEnabled } = useCodexEnabled();
  const setAgentModel = (newModel: ModelType) => {
    const newEngine = newModel.includes("/") ? "opencode" : "claude";
    console.log(
      "[IntegratedEditor] Model changed: %s → %s (engine: %s → %s)",
      agentModel,
      newModel,
      agentModel?.includes("/") ? "opencode" : "claude",
      newEngine,
    );
    _setAgentModel(newModel);

    // Persist per-project so the choice survives refresh — fire-and-forget.
    if (!projectId) return;
    void (async () => {
      try {
        const token = await getIdToken();
        if (!token) return;
        await projectsApi.updateSelectedModel(token, projectId, newModel);
      } catch (error) {
        console.debug("Failed to persist selected model:", error);
      }
    })();
  };
  const [isAgentStreaming, setIsAgentStreaming] = useState(false);

  // Fetch persisted model on mount: project override → workspace default → engine default.
  const hasLoadedPersistedModel = useRef(false);
  useEffect(() => {
    if (hasLoadedPersistedModel.current) return;

    const fetchPersistedModel = async () => {
      try {
        const token = await getIdToken();
        if (!token) return;

        // 1) Project-level override wins.
        if (projectId) {
          try {
            const { selectedModel } = await projectsApi.getSelectedModel(
              token,
              projectId,
            );
            if (selectedModel) {
              console.log(
                "[IntegratedEditor] Project selected model loaded: %s",
                selectedModel,
              );
              // Project-level choice wins over the workspace default, but a retired or
              // flag-off key (e.g. GPT-5.6 Terra) must still be remapped (#90).
              _setAgentModel(sanitizeModel(selectedModel, { codexEnabled }));
              hasLoadedPersistedModel.current = true;
              return;
            }
          } catch (error) {
            console.debug("Failed to fetch project selected model:", error);
          }
        }

        // 2) Fall back to workspace default.
        const wsId = workspaceId || getActiveWorkspaceId();
        if (!wsId) {
          hasLoadedPersistedModel.current = true;
          return;
        }

        const response = await workspacesApi.getModelSettings(token, wsId);
        if (response.defaultModel) {
          console.log(
            "[IntegratedEditor] Workspace default model loaded: %s",
            response.defaultModel,
          );
          _setAgentModel(sanitizeModel(response.defaultModel, { codexEnabled }));
        }
        hasLoadedPersistedModel.current = true;
      } catch (error) {
        console.debug("Failed to fetch persisted model:", error);
        hasLoadedPersistedModel.current = true;
      }
    };

    fetchPersistedModel();
  }, [projectId, workspaceId, getIdToken]);

  // Handle active menu item changes
  useEffect(() => {
    if (activeMenuItem === "themes") {
      // Disable visual editor selection when showing themes panel
      if (previewIframeRef.current) {
        setIsSelectionEnabled(false);
        setSelectedElement(null);
        previewMessage.toggleSelection(previewIframeRef.current, false);
        previewMessage.clearSelection(previewIframeRef.current);
      }
      setShowThemesList(true);
    }
  }, [activeMenuItem]);

  // Wrapper for streaming change that updates both local state and calls parent callback
  const handleAgentStreamingChange = useCallback(
    (streaming: boolean) => {
      setIsAgentStreaming(streaming);
      onAgentStreamingChange?.(streaming);
    },
    [onAgentStreamingChange],
  );

  const [internalShowIdeInAgentView, setInternalShowIdeInAgentView] =
    useState(false); // Track if IDE should show instead of preview in agent view - default to false (show preview)
  const showIdeInAgentView =
    externalShowPreviewInAgentView !== undefined
      ? !externalShowPreviewInAgentView
      : internalShowIdeInAgentView;

  // Create a setter that works with both internal and external control
  const setShowIdeInAgentView = useCallback(
    (value: boolean | ((prev: boolean) => boolean)) => {
      const newValue =
        typeof value === "function" ? value(showIdeInAgentView) : value;

      if (externalOnShowPreviewInAgentViewChange) {
        // If externally controlled, notify parent (invert because parent tracks showPreview, not showIde)
        externalOnShowPreviewInAgentViewChange(!newValue);
      } else {
        // Otherwise update internal state
        setInternalShowIdeInAgentView(newValue);
      }
    },
    [externalOnShowPreviewInAgentViewChange, showIdeInAgentView],
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const previewIframeRef = useRef<HTMLIFrameElement | null>(null);
  const ideIframeRef = useRef<HTMLIFrameElement>(null);
  const visualEditorRef = useRef<any>(null);
  const agentRef = useRef<AgentModeContainerHandle>(null);

  // State-tracked mirror of previewIframeRef.current. Children that need to
  // re-render when the iframe element changes (e.g. ThemePreviewCard, which
  // re-sends the theme on iframe change) must receive this state value, not
  // the ref's `.current`. ResponsiveIframeViewer remounts the <iframe>
  // element when switching between fluid mode and fixed-size viewports
  // (Mobile/Tablet/Desktop/Signage Landscape/Portrait). useRef mutations
  // don't trigger React re-renders, so without this state mirror children
  // would silently receive stale (or null) iframe references after swaps.
  const [previewIframeEl, setPreviewIframeEl] =
    useState<HTMLIFrameElement | null>(null);
  const setPreviewIframeRef = useCallback(
    (el: HTMLIFrameElement | null): void => {
      previewIframeRef.current = el;
      setPreviewIframeEl(el);
    },
    [],
  );

  // Drag-and-drop handlers for component insertion
  const {
    activeItem,
    showIconPicker,
    pendingIconDrop,
    showImagePicker,
    pendingImageDrop,
    handleDragStart,
    handleDragEnd,
    handleDragMove,
    handleIconSelected,
    handleIconPickerClose,
    handleImageSelected,
    handleImageSkip,
    handleImagePickerClose,
  } = useComponentDragDrop({ previewIframeRef });

  // Latch "has the media picker ever been opened?" so we can keep the popup
  // mounted after the first open. Staying mounted preserves its hook state
  // (generated images / project assets) across close→reopen — no refetch.
  const [hasOpenedMediaPicker, setHasOpenedMediaPicker] = useState(false);
  useEffect(() => {
    if (showImagePicker && !hasOpenedMediaPicker) {
      setHasOpenedMediaPicker(true);
    }
  }, [showImagePicker, hasOpenedMediaPicker]);

  // Expose agent sendPrompt function to parent via callback
  useEffect(() => {
    if (onAgentSendPrompt) {
      const sendFn = (text: string) => {
        if (agentRef.current) {
          agentRef.current.sendPrompt(text);
        } else {
          // Switch to agent mode and retry after mount
          setViewMode("agent");
          setTimeout(() => {
            agentRef.current?.sendPrompt(text);
          }, 100);
        }
      };
      onAgentSendPrompt(sendFn);
    }
    return () => {
      if (onAgentSendPrompt) {
        onAgentSendPrompt(null);
      }
    };
  }, [onAgentSendPrompt, setViewMode]);

  // Honor external requests to open the agent panel (e.g. from a slide
  // thumbnail's "Edit with Agent" action). The agent-input store flips
  // `shouldOpenPanel` synchronously when a mention is enqueued, so by the
  // time AgentChat mounts the chip queue is already non-empty and its
  // subscribe-effect drains it on first paint.
  const shouldOpenAgent = useAgentInputStore((s) => s.shouldOpenPanel);
  const clearAgentOpenSignal = useAgentInputStore((s) => s.clearOpenSignal);
  useEffect(() => {
    if (!shouldOpenAgent) return;
    if (viewMode !== "agent") setViewMode("agent");
    clearAgentOpenSignal();
  }, [shouldOpenAgent, viewMode, setViewMode, clearAgentOpenSignal]);

  // Expose mobile settings opener function to parent via callback
  // Opens directly to Project Settings drawer (skips the menu sheet)
  useEffect(() => {
    if (onMobileSettingsOpener) {
      const openFn = (
        section?: "info" | "deploy" | "optidev-cloud" | "agent-settings" | "reports",
      ) => {
        setProjectSettingsSection(section || "info");
        setShowProjectSettingsDrawer(true);
      };
      onMobileSettingsOpener(openFn);
    }
    return () => {
      if (onMobileSettingsOpener) {
        onMobileSettingsOpener(null);
      }
    };
  }, [onMobileSettingsOpener]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );
  const history = useVisualEditorHistoryStore((state) => state.history);
  const currentIndex = useVisualEditorHistoryStore(
    (state) => state.currentIndex,
  );
  const pushHistory = useVisualEditorHistoryStore((state) => state.pushHistory);

  // Token cleanup function with cookie polling
  const cleanIframeToken = useCallback((iframeElement: HTMLIFrameElement) => {
    let attempts = 0;

    const checkAndClean = () => {
      attempts++;

      try {
        const iframeWin = iframeElement.contentWindow;

        if (!iframeWin || !iframeWin.location) {
          return; // Can't access iframe window
        }

        // Check if URL has token parameter
        const hasToken = iframeWin.location.search.includes("tkn=");
        if (!hasToken) {
          return; // No token to clean
        }

        // Check if authentication cookie exists in iframe
        const hasCookie = iframeWin.document.cookie.includes("optidev_session");

        if (hasCookie) {
          // Cookie found - clean the URL
          const url = new URL(iframeWin.location.href);
          url.searchParams.delete("tkn");
          iframeWin.history.replaceState(
            {},
            "",
            url.pathname + url.search + url.hash,
          );

          return; // Done!
        }

        // No cookie yet - try again if within limit
        if (attempts < TOKEN_CLEANUP_MAX_ATTEMPTS) {
          setTimeout(checkAndClean, TOKEN_CLEANUP_INTERVAL);
        } else {
        }
      } catch (e) {
        // Cross-origin restrictions - expected for some cases
        console.log(
          "[OptiDev] Token cleanup skipped due to cross-origin policy",
        );
      }
    };

    // Start checking after a small delay to ensure iframe is fully loaded
    setTimeout(checkAndClean, 100);
  }, []);

  // Get actual URLs for development and production - memoized
  const actualIdeUrl = useMemo(() => {
    return isDevelopment ? "http://localhost:8080" : workspaceUrl;
  }, [isDevelopment, workspaceUrl]);

  // No need for useMemo - previewUrl is already computed by parent
  // Just use it directly

  // Track whether path change originated from within iframe
  const isIframeNavigationRef = useRef(false);

  // Reload iframe when preview URL changes (handles route changes)
  useEffect(() => {
    const iframe = previewIframeRef.current;
    if (!iframe || !previewUrl) return;

    // Skip reload if this path change originated from within the iframe
    if (isIframeNavigationRef.current) {
      console.log(
        "[IntegratedEditor] Skipping iframe reload - path change from internal navigation",
      );
      return;
    }

    // Only reload if the URL actually changed
    if (iframe.src !== previewUrl) {
      console.log(
        "[IntegratedEditor] Reloading iframe with new URL:",
        previewUrl,
      );
      iframe.src = previewUrl;
    }
  }, [previewUrl]);

  // Backward compatibility wrappers
  const getActualIdeUrl = useCallback(() => actualIdeUrl, [actualIdeUrl]);
  // Don't load real preview URL if container isn't ready (prevents error JSON in iframe during resume)
  const getActualPreviewUrl = useCallback(() => previewUrl, [previewUrl]);

  // Handle opening in new window
  const handleOpenIdeInNewWindow = () => {
    // Use the actual workspace URL if provided (for production URLs in dev mode)
    // Otherwise fall back to workspaceUrl
    const urlToOpen = actualWorkspaceUrl || workspaceUrl;
    window.open(urlToOpen, "_blank");
  };

  const handleOpenPreviewInNewWindow = () => {
    if (externalOnOpenPreviewInNewWindow) {
      externalOnOpenPreviewInNewWindow();
      return;
    }
    const fallback = getActualPreviewUrl();
    if (!devContainerId) {
      window.open(fallback, "_blank");
      return;
    }
    const service = extractServiceSuffix(fallback);
    openWithFreshToken(fallback, () =>
      service
        ? devContainerAPI.getServiceAccessUrl(devContainerId, service)
        : devContainerAPI.getDevContainerAccessUrl(devContainerId),
    );
  };

  // Handle opening a file in VS Code iframe
  const handleOpenFileInIde = useCallback((filePath: string) => {
    console.log("[IntegratedEditor] Switching to IDE view for file:", filePath);

    // Show IDE instead of preview in agent view
    setShowIdeInAgentView(true);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleLeftMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizingLeft(true);
  };

  // Force re-render TransformWrapper when responsive mode changes
  useEffect(() => {
    setTransformKey((prev) => prev + 1);
  }, [isResponsiveMode]);

  // Handle preview reload
  const handleReloadPreview = () => {
    if (externalOnReloadPreview) {
      externalOnReloadPreview();
    } else if (previewIframeRef.current) {
      previewIframeRef.current.src = previewIframeRef.current.src;
    }
  };

  // Handle undo
  const handleUndo = () => {
    if (visualEditorRef.current?.handleUndo) {
      visualEditorRef.current.handleUndo();
    }
  };

  // Handle redo
  const handleRedo = () => {
    if (visualEditorRef.current?.handleRedo) {
      visualEditorRef.current.handleRedo();
    }
  };

  // Handle IDE fullscreen toggle
  const handleToggleFullscreenIde = () => {
    setIsFullscreenIde(!isFullscreenIde);
  };

  // Handle preview fullscreen toggle
  const handleToggleFullscreenPreview = () => {
    setIsFullscreenPreview(!isFullscreenPreview);
  };

  // Handle fluid select (exit responsive mode)
  const handleFluidSelect = () => {
    setIsResponsiveMode(false);
    // Force re-render TransformWrapper to reset all state
    setTransformKey((prev) => prev + 1);
  };

  // Handle resize start
  const handleResizeStart = () => {
    // Disable panning when user starts resizing
    setIsPanningDisabled(true);
  };

  // Handle resize stop
  const handleResizeStop = (width: number, height: number) => {
    // Re-enable panning when user stops resizing
    setIsPanningDisabled(false);

    // Notify parent about new dimensions
    if (onViewportResize) {
      onViewportResize(width, height);
    }
  };

  // Handle enable selection toggle
  const handleToggleSelection = (enabled: boolean) => {
    // Don't enable selection for viewers (read-only access)
    if (!canEditFiles && enabled) return;
    setIsSelectionEnabled(enabled);
    previewMessage.toggleSelection(previewIframeRef.current, enabled);
  };

  const handleReloadLayers = () => {
    previewMessage.requestLayers(previewIframeRef.current);
  };

  // Handle show connections modal
  const handleSelectConnections = () => {
    setShowConnectionsModal(true);
  };

  // Handle show project settings
  const handleSelectProjectSettings = () => {
    handleViewModeChange("settings");
  };

  // Handle show image generator
  const handleSelectImageGenerator = () => {
    handleViewModeChange("design");
  };

  // Handle show media library
  const handleSelectMediaLibrary = () => {
    handleViewModeChange("media");
  };

  const handleSelectOptisignsAssets = () => {
    handleViewModeChange("optisignsAssets");
  };

  // OptiSigns assets picked in the browse panel, buffered until AgentChat merges
  // them into the composer as @asset chips (then it calls the consumed callback).
  const [pendingAssetRefs, setPendingAssetRefs] = useState<
    { assetId: string; name: string }[]
  >([]);
  const handleOptisignsAssetsSelected = (
    refs: { assetId: string; name: string }[],
  ) => {
    if (refs.length === 0) return;
    setPendingAssetRefs(refs);
    handleViewModeChange("agent"); // return to chat so the chips are visible
  };

  // Handle show themes
  const handleSelectThemes = () => {
    if (onActiveMenuItemChange) {
      onActiveMenuItemChange("themes");
    }
  };

  // Memoize trusted origins check
  const isTrustedOrigin = useCallback(
    (origin: string): boolean => {
      const trustedOrigins = [...LOCAL_PREVIEW_URLS, previewUrl];
      return (
        trustedOrigins.includes(origin) ||
        TRUSTED_DOMAINS.some((domain) => origin.endsWith(domain))
      );
    },
    [previewUrl],
  );

  // Handle messages from preview iframe
  const handlePreviewMessage = useCallback(
    async (event: MessageEvent) => {
      // Only accept messages from the preview iframe
      if (event.source !== previewIframeRef.current?.contentWindow) {
        return;
      }

      // Validate origin is from trusted sources
      if (!isTrustedOrigin(event.origin)) {
        console.warn(
          "Ignoring message from untrusted preview origin:",
          event.origin,
        );
        return;
      }
      const messageData = event.data;

      // If we receive any message from preview while in loading state, it means preview is responding
      // Call onPreviewLoaded to stop the loading state
      if (isPreviewLoading && onPreviewLoaded) {
        onPreviewLoaded();
      }

      if (messageData.type === PreviewMessageType.ELEMENT_SELECTED) {
        const elementData: ElementData = {
          tagName: messageData.tagName || "div",
          bind: messageData.bind,
          content: messageData.text || "",
          styles: messageData.styles || {},
          source: messageData.source
            ? {
                fileName: messageData.source.split(":")[0].replace(/^\/+/, "/"),
                lineNumber: parseInt(
                  messageData.source.split(":")[1] || "0",
                  10,
                ),
                columnNumber: parseInt(
                  messageData.source.split(":")[2] || "0",
                  10,
                ),
              }
            : undefined,
          path: messageData.source || "",
          attributes: messageData.attributes || {},
          className: messageData.className || "",
        };

        if (messageData.isAdditive) {
          // Cmd+Click: accumulate multi-selection
          setSelectedElements((prev) => {
            const exists = prev.some((el) => el.path === elementData.path);
            if (exists) {
              return prev.filter((el) => el.path !== elementData.path);
            }
            return [...prev, elementData];
          });
          // Keep selectedElement pointing to the last clicked for toolbar position
          setSelectedElement(elementData);

          // Show floating toolbar at the last clicked element
          if (messageData.bounds) {
            setToolbarBounds(messageData.bounds);
            setToolbarVisible(true);
          }
        } else {
          // Regular click: replace selection
          setSelectedElements([elementData]);

          if (visualEditorRef.current) {
            visualEditorRef.current.handleElementSelected(elementData);
          }

          // Update selectedElement state for FloatingToolbar
          setSelectedElement(elementData);

          // Show floating toolbar with element bounds
          if (messageData.bounds) {
            setToolbarBounds(messageData.bounds);
            setToolbarVisible(true);
          }
          handleReloadLayers();
        }
      } else if (
        messageData.type === PreviewMessageType.CLEAR_SELECTION &&
        !messageData.fromHost
      ) {
        // Escape key in iframe: clear host selection state
        setSelectedElement(null);
        setSelectedElements([]);
        setToolbarVisible(false);
        if (visualEditorRef.current) {
          visualEditorRef.current.handleElementSelected(null);
        }
      } else if (messageData.type === PreviewMessageType.CONTENT_CHANGED) {
        if (visualEditorRef.current?.handleChangeContentCallback) {
          visualEditorRef.current.handleChangeContentCallback(
            messageData.text || "",

            // Show floating toolbar with element bounds
          );
          if (messageData.bounds) {
            setToolbarBounds(messageData.bounds);
            setToolbarVisible(true);
          }
        }
      } else if (messageData.type === PreviewMessageType.ELEMENT_RESIZED) {
        // Update PropertyPanel with new styles (width, height, transform)
        if (visualEditorRef.current?.handleChangeStylesCallback) {
          const styles: Record<string, string> = {};
          if (messageData?.styles?.width) {
            styles.width = messageData.styles.width;
          }
          if (messageData?.styles?.height) {
            styles.height = messageData.styles.height;
          }
          if (messageData?.styles?.transform) {
            styles.transform = messageData.styles.transform;
          }
          if (Object.keys(styles).length > 0) {
            visualEditorRef.current.handleChangeStylesCallback(styles);
          }
        }
      } else if (messageData.type === PreviewMessageType.LAYER_ANALYSIS) {
        if (visualEditorRef.current?.handleLayersUpdate) {
          visualEditorRef.current.handleLayersUpdate(
            messageData.layers?.rootElements || [],
          );
        }
      } else if (messageData.type === PreviewMessageType.DRAG_ENABLED) {
        const elementData: ElementData = {
          tagName: messageData.tagName || "div",
          bind: messageData.bind,
          content: messageData.text || "",
          styles: {
            ...(messageData.styles || {}),
            width: messageData?.styles?.width,
            height: messageData?.styles?.height,
            // position: "absolute",
            // left: messageData?.bounds.left || 0,
            // top: messageData?.bounds.top || 0,
            // bottom: messageData?.bounds.bottom || 0,
            // right: messageData?.bounds.right || 0,
          },
          source: messageData.source
            ? {
                fileName: messageData.source.split(":")[0].replace(/^\/+/, "/"),
                lineNumber: parseInt(
                  messageData.source.split(":")[1] || "0",
                  10,
                ),
                columnNumber: parseInt(
                  messageData.source.split(":")[2] || "0",
                  10,
                ),
              }
            : undefined,
          path: messageData.source || "",
          attributes: messageData.attributes || {},
          className: messageData.className || "",
        };

        setSelectedElement(elementData);
      } else if (messageData.type === PreviewMessageType.COMPONENT_ADDED) {
        const elementInfo = messageData.elementInfo;
        const elementPath = messageData.source;
        const addedElementData: ElementData = {
          tagName: elementInfo.tagName || "div",
          bind: elementInfo.bind,
          content: elementInfo.text || elementInfo.content || "",
          styles: elementInfo.styles || {},
          attributes: elementInfo.attributes || {},
          className: elementInfo.className || "",
          source: elementInfo.source,
          path: elementPath,
          children: elementInfo.children,
          rawJsx: messageData.rawJsx || elementInfo.rawJsx || undefined,
          iconName: elementInfo.iconName || undefined,
        };

        // Store structure change info for undo/redo
        const structureChange: StructureChangeType = {
          parentBefore: null,
          parentAfter: messageData.newParentId,
          positionBefore: null,
          positionAfter: messageData.position ?? messageData.dropPosition,
          dropPosition: messageData.dropPosition || null,
          targetElementId: messageData.targetElementId || null,
          isDuplicate: messageData.isDuplicate || false,
        };
        pushHistory(
          elementPath,
          elementPath,
          {
            text: elementInfo.text || elementInfo.content || "",
            styles: elementInfo.styles || {},
            attributes: elementInfo.attributes || {},
          },
          addedElementData,
          "add",
          structureChange,
        );
        // Set selected element immediately so handleSave has a valid element.
        // selectByPath triggers an async round-trip (postMessage → iframe →
        // ELEMENT_SELECTED → setSelectedElement) that may not complete before
        // the auto-save timeout fires, causing handleSave to bail with
        // "No element selected".
        setSelectedElement(addedElementData);
        previewMessage.selectByPath(
          previewIframeRef.current,
          elementInfo.source,
        );

        // Auto-save icon and rawJsx (section/nav) components immediately.
        // Each is saved as a single operation so subsequent children that
        // reference generated IDs resolve to real file paths, avoiding
        // batch line-shift and generated-ID resolution bugs.
        // Skip for duplicates (Ctrl+C/V) — they don't need immediate save.
        const isDuplicate = messageData.isDuplicate || false;
        if (
          !isDuplicate &&
          (addedElementData.iconName || addedElementData.rawJsx) &&
          visualEditorRef.current?.handleSave
        ) {
          setTimeout(() => {
            visualEditorRef.current?.handleSave();
          }, 100);
        }
      } else if (messageData.type === PreviewMessageType.ELEMENTS_REORDERED) {
        const fromPath = messageData.fromPath || messageData.elementPath;
        const toPath = messageData.toPath || messageData.targetPath;
        const source = parseSourceLocation(fromPath);
        const target = parseSourceLocation(toPath);
        // Keep the raw drop position (left/right/top/bottom) for the dev-container.
        // normalizeDropPosition is only used for fallback when dropPosition is missing.
        const rawDropPosition = messageData.dropPosition as
          | "top"
          | "bottom"
          | "left"
          | "right"
          | undefined;
        const normalizedPosition = normalizeDropPosition(
          messageData.dropPosition,
        );

        if (!source || !target) {
          console.error(
            "Invalid source or target location for elements-reordered",
          );
          return;
        }

        // Convert position to valid batch API format (top/bottom only) for history tracking
        const batchPosition =
          normalizedPosition === "after" || normalizedPosition === "inside"
            ? "bottom"
            : (normalizedPosition as "top" | "bottom");

        // Use element data from VisualEditor (via message.elementData) or from message itself
        const elementData = messageData.elementData || {};
        const movedElement: ElementData = {
          tagName: elementData.tagName || messageData.tagName || "div",
          bind: elementData.bind || messageData.bind,
          content:
            elementData.text || messageData.text || messageData.content || "",
          styles: messageData.styles || {},
          attributes: messageData.attributes || {},
          className: elementData.className || messageData.className || "",
          source: source,
          path: fromPath,
        };

        // Store structure change info for undo/redo
        // dropPosition keeps the raw value so the dev-container can distinguish
        // left/right (insert inside) from top/bottom (sibling or container child)
        const structureChange: StructureChangeType = {
          parentBefore: messageData.oldParentId || null,
          parentAfter: toPath,
          positionBefore: messageData.oldPosition ?? null,
          positionAfter: batchPosition,
          targetElementId: toPath,
          dropPosition: rawDropPosition || batchPosition,
        };

        pushHistory(
          fromPath,
          fromPath,
          {
            text: movedElement.content,
            styles: movedElement.styles,
            attributes: movedElement.attributes,
          },
          movedElement,
          "move",
          structureChange,
        );
      } else if (messageData.type === PreviewMessageType.DRAG_END) {
        const source = parseSourceLocation(messageData.source);
        const target = parseSourceLocation(messageData.targetElementId);
        if (!source || !target) {
          console.error("Invalid source or target location for drag-end");
          return;
        }

        const elementData: ElementData = {
          tagName: messageData.tagName || "div",
          bind: messageData.bind,
          content: messageData.text || "",
          styles: messageData.styles || {},
          source: source,
          path: messageData.source || "",
          attributes: messageData.attributes || {},
          className: messageData.className || "",
        };

        const structureChange: StructureChangeType = {
          parentBefore: messageData.originalParentId || null,
          parentAfter: messageData.newParentId || null,
          positionBefore: messageData.originalPosition ?? null,
          positionAfter: messageData.newPosition ?? null,
          dropPosition: messageData.dropPosition || null,
          targetElementId: messageData.targetElementId || null,
        };

        pushHistory(
          messageData.source,
          messageData.source,
          {
            styles: messageData.styles || {},
            attributes: messageData.attributes || {},
          },
          elementData,
          "move",
          structureChange,
        );
      } else if (messageData.type === PreviewMessageType.COMPONENT_DELETED) {
        const source = parseSourceLocation(messageData.source);
        if (!source) {
          console.error("Invalid source location for component-deleted");
          return;
        }

        // Create element data for the deleted component
        const deletedElementData: ElementData = {
          tagName: messageData.tagName || "div",
          bind: messageData.bind,
          content: messageData.text || messageData.content || "",
          styles: messageData.styles || {},
          source: source,
          path: messageData.source || "",
          attributes: messageData.attributes || {},
          className: messageData.className || "",
        };

        // Store parent and position information for undo/redo
        const structureChange: StructureChangeType = {
          parentBefore: messageData.parentId || null,
          parentAfter: null,
          positionBefore: messageData.position ?? null,
          positionAfter: null,
        };

        pushHistory(
          messageData.source,
          messageData.source,
          {
            text: messageData.text || messageData.content || "",
            styles: messageData.styles || {},
            attributes: messageData.attributes || {},
          },
          deletedElementData,
          "delete",
          structureChange,
          messageData.bulkDeleteId,
        );
      } else if (messageData.type === PreviewMessageType.UNDO_REQUEST) {
        handleUndo();
      } else if (messageData.type === PreviewMessageType.REDO_REQUEST) {
        handleRedo();
      } else if (messageData.type === PreviewMessageType.COPY_ELEMENT) {
        // Data-bound elements cannot be duplicated — only editable via AI
        if (selectedElement?.path && !isDataBoundElement(selectedElement)) {
          copiedElementPathRef.current = selectedElement.path;
        }
      } else if (messageData.type === PreviewMessageType.PASTE_ELEMENT) {
        if (copiedElementPathRef.current && previewIframeRef.current) {
          const newId = "optidev-temp-" + crypto.randomUUID();
          previewMessage.duplicateElement(
            previewIframeRef.current,
            copiedElementPathRef.current,
            newId,
          );
        }
      } else if (messageData.type === PreviewMessageType.ROUTE_CHANGED) {
        // Handle route change from iframe
        // Set flag to indicate this path change originated from iframe navigation
        isIframeNavigationRef.current = true;
        if (onPathChange && messageData.path) {
          onPathChange(messageData.path);
        }

        // Request layers after route change is complete
        if (previewIframeRef?.current) {
          previewMessage.requestLayers(previewIframeRef.current);
        }

        // Reset flag after a short delay to allow effects to run
        setTimeout(() => {
          isIframeNavigationRef.current = false;
        }, 100);
      } else if (messageData.type === PreviewMessageType.OPTIDEV_FIX_ERROR) {
        // Handle "Fix Issue" button click from Vite error overlay
        const errorMessage =
          messageData.formattedMessage ||
          messageData.error?.message ||
          "Unknown error";
        const fixPrompt = `Fix this error:\n\n\`\`\`\n${errorMessage}\n\`\`\`\n\nAfter fixing this error, please also check for and resolve any follow-up errors that may appear (such as type errors, missing imports, or cascading issues). Additionally, check the browser console for any related errors and fix those as well.`;

        // Send to agent
        if (agentRef.current) {
          agentRef.current.sendPrompt(fixPrompt);
        } else {
          console.warn(
            "[IntegratedEditor] Agent ref not available to send fix prompt",
          );
        }
      } else if (messageData.type === PreviewMessageType.SCROLL_POSITION) {
        // Handle scroll position updates from iframe
        // Hide viewport controls when near bottom on mobile preview tab
        // (Header is already hidden immediately on tab switch)
        if (isMobile && mobileActiveTab === "preview") {
          const shouldHide = messageData.isNearBottom;
          setIsMobileTabSwitcherVisible(!shouldHide);
        }
      }
    },
    [
      isTrustedOrigin,
      selectedElement,
      pushHistory,
      handleUndo,
      handleRedo,
      onPathChange,
      isPreviewLoading,
      onPreviewLoaded,
      isMobile,
      mobileActiveTab,
    ],
  );

  // Enable/disable selection when switching modes
  const handleViewModeChange = useCallback(
    (mode: ViewMode) => {
      setViewMode(mode);

      if (previewIframeRef.current?.contentWindow && previewLoaded) {
        if (mode === "visual") {
          // Start with selection enabled in visual mode (only if user can edit files)
          if (canEditFiles) {
            setIsSelectionEnabled(true);
            previewMessage.toggleSelection(previewIframeRef.current, true);
            previewMessage.requestLayers(previewIframeRef.current);
          }
        } else {
          // When leaving visual mode, disable selection and clean up
          setIsSelectionEnabled(false);
          setSelectedElement(null); // Clear selected element
          setToolbarVisible(false); // Hide floating toolbar
          previewMessage.toggleSelection(previewIframeRef.current, false);
          // Remove any visual selection indicators (outlines, etc.)
          previewMessage.clearSelection(previewIframeRef.current);
        }
      }
    },
    [previewLoaded, canEditFiles],
  );

  const onSelectParent = () => {
    if (previewIframeRef.current) {
      previewMessage.selectParent(previewIframeRef.current);
    }
  };

  // Z-index ordering handlers — updates pendingChanges/history AND applies visually to iframe
  const applyZIndexChange = useCallback(
    (delta: number) => {
      if (
        !selectedElement ||
        !visualEditorRef.current?.handleChangeStylesCallback
      )
        return;
      const current = parseInt(selectedElement.styles?.zIndex || "0", 10) || 0;
      const newZIndex = String(current + delta);
      // z-index only works on positioned elements — ensure position: relative if static
      const needsPosition =
        !selectedElement.styles?.position ||
        selectedElement.styles.position === "static";
      const styleChanges: Record<string, string> = { zIndex: newZIndex };
      if (needsPosition) {
        styleChanges.position = "relative";
      }
      // Update pending changes + history via VisualEditor
      visualEditorRef.current.handleChangeStylesCallback(styleChanges);
      // Apply visually to iframe preview
      const updatedElement = {
        ...selectedElement,
        styles: { ...selectedElement.styles, ...styleChanges },
      };
      previewMessage.updateElement(previewIframeRef.current, updatedElement);
      // Update local state so subsequent clicks use the new value
      setSelectedElement(updatedElement);
    },
    [selectedElement],
  );

  const handleBringToFront = useCallback(
    () => applyZIndexChange(10),
    [applyZIndexChange],
  );
  const handleBringForward = useCallback(
    () => applyZIndexChange(1),
    [applyZIndexChange],
  );
  const handleSendBackward = useCallback(
    () => applyZIndexChange(-1),
    [applyZIndexChange],
  );
  const handleSendToBack = useCallback(
    () => applyZIndexChange(-10),
    [applyZIndexChange],
  );

  const handleDuplicate = useCallback(() => {
    if (!selectedElement?.path || !previewIframeRef.current) return;
    if (isDataBoundElement(selectedElement)) return;
    const newId = "optidev-temp-" + crypto.randomUUID();
    previewMessage.duplicateElement(
      previewIframeRef.current,
      selectedElement.path,
      newId,
    );
  }, [selectedElement]);

  const copiedElementPathRef = useRef<string | null>(null);

  const onAiCommand = useCallback(
    (command: string) => {
      if (!selectedElement) {
        console.warn("[IntegratedEditor] No element selected");
        return;
      }

      // Check if element has source location - required for visual editor
      if (!selectedElement.source) {
        console.warn(
          "[IntegratedEditor] Element has no source location, cannot edit",
        );
        return;
      }
      // Format element context with source location
      const elementContext: Record<string, any> = {
        elementPath: selectedElement.path,
        elementTag: selectedElement.tagName,
        elementContent: selectedElement.content || "",
        elementStyles: selectedElement.styles || {},
        elementClassName: selectedElement.className || "",
        sourceLocation: {
          fileName: selectedElement.source.fileName,
          lineNumber: selectedElement.source.lineNumber,
          columnNumber: selectedElement.source.columnNumber,
        },
      };

      // Add bind information if element has data binding
      if (selectedElement.bind) {
        elementContext.bind = {
          symbol: selectedElement.bind.symbol,
          index: selectedElement.bind.index,
          property: selectedElement.bind.property,
          ...(selectedElement.bind.nestedIndex !== undefined && {
            nestedIndex: selectedElement.bind.nestedIndex,
          }),
        };
      }

      // Build prompt with element context for visual editor
      const prompt = `${command}

<element_context>
${JSON.stringify(elementContext, null, 2)}
</element_context>`;

      // If agentRef is not available, switch to agent mode first
      if (!agentRef.current) {
        setViewMode("agent");

        // Clear visual selection when switching to agent mode
        if (previewIframeRef.current) {
          setIsSelectionEnabled(false);
          setSelectedElement(null);
          setToolbarVisible(false);
          previewMessage.clearSelection(previewIframeRef.current);
        }

        // Wait for next tick to ensure AgentModeContainer is mounted
        setTimeout(() => {
          if (agentRef.current) {
            agentRef.current.sendPrompt(prompt);
          }
        }, 100);
      } else {
        agentRef.current.sendPrompt(prompt);
      }
    },
    [selectedElement, setViewMode],
  );
  // Handle preview iframe load
  const handlePreviewLoad = useCallback(() => {
    setToolbarVisible(false); // Hide toolbar when leaving visual mode
    setPreviewLoaded(true);

    // Only notify parent if we're NOT in preview loading state
    // When isPreviewLoading=true, we're waiting for Vite to start and will detect success via message handler
    // The 503 error page also fires onLoad, so we can't trust this event during initial loading
    if (onPreviewLoaded && !isPreviewLoading) {
      onPreviewLoaded();
    }

    // Clean token from preview iframe URL
    if (previewIframeRef.current) {
      cleanIframeToken(previewIframeRef.current);
    }

    // Use postMessage for cross-origin communication
    if (previewIframeRef.current?.contentWindow) {
      // Re-enable selection if it was enabled before reload (only if user can edit files)
      previewMessage.toggleSelection(
        previewIframeRef.current,
        isSelectionEnabled && canEditFiles,
      );

      // Initialize theme once when iframe loads (fonts cached for subsequent updateTheme calls)
      previewMessage.initTheme(previewIframeRef.current, GOOGLE_FONTS);

      // Re-apply the current theme CSS after every iframe load (handles remounts from viewport switches)
      const themeHistory = useThemeHistoryStore.getState();
      const currentThemeSnapshot = themeHistory.history[themeHistory.currentIndex];
      if (currentThemeSnapshot?.content) {
        previewMessage.updateTheme(
          previewIframeRef.current,
          currentThemeSnapshot.themeId || "imported",
          currentThemeSnapshot.content,
        );
      }

      // Inject Tailwind CDN for dynamic class support in visual editor
      previewMessage.injectTailwindCdn(previewIframeRef.current);

      // Apply all pending changes to the newly loaded iframe
      const allChanges = getAllPendingChanges(history, currentIndex);
      if (allChanges.size > 0) {
        allChanges.forEach(
          (changeData: {
            element: ElementData;
            changes: any;
            originalElement?: ElementData;
            actionType: "update" | "add" | "delete" | "move";
            structureChange?: any;
          }) => {
            const { element, actionType } = changeData;

            if (actionType === "update" && element) {
              // Apply pending updates to the iframe
              previewMessage.updateElement(previewIframeRef.current, element);
            }
          },
        );
      }
    }
  }, [
    cleanIframeToken,
    isSelectionEnabled,
    history,
    currentIndex,
    onPreviewLoaded,
    isPreviewLoading,
  ]);

  // Handle IDE iframe load
  const handleIdeLoad = useCallback(() => {
    setIdeLoaded(true);

    // Clean token from IDE iframe URL
    if (ideIframeRef.current) {
      cleanIframeToken(ideIframeRef.current);
    }
  }, [cleanIframeToken]);

  // Reset previewLoaded when previewUrl changes to show loading spinner
  useEffect(() => {
    // Skip reset if this path change originated from within the iframe
    if (isIframeNavigationRef.current) {
      return;
    }
    setPreviewLoaded(false);
  }, [previewUrl]);

  // Setup message listener
  useEffect(() => {
    window.addEventListener("message", handlePreviewMessage);
    return () => {
      window.removeEventListener("message", handlePreviewMessage);
    };
  }, [handlePreviewMessage]);

  // Enable selection when preview loads in visual mode (desktop or mobile)
  useEffect(() => {
    const isVisualMode =
      viewMode === "visual" || (isMobile && mobileActiveTab === "visual");
    if (
      isVisualMode &&
      previewLoaded &&
      previewIframeRef.current?.contentWindow &&
      canEditFiles // Only enable selection if user can edit files
    ) {
      // Enable selection when preview is ready
      setIsSelectionEnabled(true);
      previewMessage.toggleSelection(previewIframeRef.current, true);
      previewMessage.requestLayers(previewIframeRef.current);
    }
  }, [viewMode, previewLoaded, isMobile, mobileActiveTab, canEditFiles]);

  // Cleanup when leaving visual mode (watch viewMode changes and mobile tab)
  useEffect(() => {
    const isVisualMode =
      viewMode === "visual" || (isMobile && mobileActiveTab === "visual");
    if (!isVisualMode && previewIframeRef.current?.contentWindow) {
      // Force disable selection mode and clear all visual indicators
      setIsSelectionEnabled(false);
      setSelectedElement(null);
      setToolbarVisible(false); // Hide floating toolbar

      previewMessage.toggleSelection(previewIframeRef.current, false);
      previewMessage.clearSelection(previewIframeRef.current);
    }
  }, [viewMode, isMobile, mobileActiveTab]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const newPosition =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      if (isResizing) {
        // Keep split between limits
        setSplitPosition(
          Math.min(
            SPLIT_POSITION_LIMITS.max,
            Math.max(SPLIT_POSITION_LIMITS.min, newPosition),
          ),
        );
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  // Drag handler for the left sidebar (chat) <-> right preview divider
  useEffect(() => {
    if (!isResizingLeft) return;

    // While dragging we follow the cursor freely (no snapping) so the panel can
    // expand smoothly from 0. The magnet snap is evaluated only on release: if
    // the divider is let go inside the threshold band it snaps fully closed
    // (0 / 100). This lets a collapsed side reopen from 0 yet still snap back
    // shut if the user releases before crossing the threshold.
    let lastWidth = leftPanelWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const rawWidth =
        ((e.clientX - containerRect.left) / containerRect.width) * 100;

      lastWidth = Math.min(
        LEFT_PANEL_WIDTH_LIMITS.max,
        Math.max(LEFT_PANEL_WIDTH_LIMITS.min, rawWidth),
      );
      setLeftPanelWidth(lastWidth);
    };

    const handleMouseUp = () => {
      // Magnet snap on release: collapse the side fully if let go below threshold.
      let snapped = lastWidth;
      if (lastWidth < LEFT_PANEL_SNAP_THRESHOLD) {
        snapped = 0; // chat collapsed -> preview full
      } else if (lastWidth > 100 - LEFT_PANEL_SNAP_THRESHOLD) {
        snapped = 100; // preview collapsed -> chat full
      }
      if (snapped !== lastWidth) {
        setLeftPanelWidth(snapped);
      }
      setIsResizingLeft(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizingLeft]);

  // Track the Visual tab row width so the tabs can collapse to icons when narrow
  useEffect(() => {
    if (viewMode !== "visual") return;
    const el = visualHeaderRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setVisualHeaderWidth(entries[0]?.contentRect.width ?? 0);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [viewMode]);

  // Expose visual editor ref to parent component
  useEffect(() => {
    if (onVisualEditorRef) {
      onVisualEditorRef(visualEditorRef);
    }
  }, [onVisualEditorRef]);

  // Expose preview iframe ref to parent component
  useEffect(() => {
    if (onPreviewIframeRef) {
      onPreviewIframeRef(previewIframeRef);
    }
  }, [onPreviewIframeRef]);

  // Reset mobile tab when responsive state changes
  useEffect(() => {
    if (isMobile !== undefined) {
      setMobileActiveTab("chat");
    }
  }, [isMobile]);

  // Control header and tab switcher visibility based on active tab
  useEffect(() => {
    if (isMobile) {
      if (mobileActiveTab === "preview") {
        // Hide header immediately on preview tab
        if (onHeaderVisibilityChange) {
          onHeaderVisibilityChange(false);
        }
        // Show viewport controls initially
        setIsMobileTabSwitcherVisible(true);
      } else {
        // Show header on other tabs (chat, visual)
        if (onHeaderVisibilityChange) {
          onHeaderVisibilityChange(true);
        }
        setIsMobileTabSwitcherVisible(true);
      }
    }
  }, [mobileActiveTab, onHeaderVisibilityChange, isMobile]);

  // Sync theme with IDE when theme changes
  useEffect(() => {
    if (!devContainerId || !resolvedTheme || !isContainerReady) return;

    // Sync theme with IDE
    const syncTheme = async () => {
      try {
        const token = await getIdToken();
        if (!token) {
          console.warn("[IntegratedEditor] No token available for theme sync");
          return;
        }
        await claudeAgentApi.updateTheme(token, devContainerId, resolvedTheme);

        // Reload IDE iframe to apply the new theme
        // VS Code/code-server doesn't auto-reload when settings.json changes
        if (ideIframeRef.current) {
          setIdeLoaded(false); // Show loading spinner
          ideIframeRef.current.src = ideIframeRef.current.src;
        }
      } catch (error) {
        // Silently fail - theme sync is not critical
        console.warn("[IntegratedEditor] Failed to sync IDE theme:", error);
      }
    };

    syncTheme();
  }, [resolvedTheme, devContainerId, getIdToken, isContainerReady]);

  // Fetch slide manifest on project load (replaces postMessage-based detection).
  //
  // Gate on `previewLoaded`, not `previewUrl`. Container handover hydrates the
  // workspace ~minutes after `previewUrl` is computed; if we fetch on
  // previewUrl, the sidecar /read-file returns 404 for both new and legacy
  // manifest paths (no files yet), the fallback gives up, and isSlideProject
  // gets flipped to false — hiding the slides panel until the user refreshes.
  // `previewLoaded` only flips true on iframe onLoad, which means Vite is
  // serving real content, which means the workspace is hydrated and the
  // manifest read will resolve correctly.
  useEffect(() => {
    if (!devContainerId || !previewLoaded) return;
    // Set editor context so auto-commit handler can re-fetch slides
    setEditorContext({ devContainerId, getIdToken });
    useSlideStore.getState().bumpThumbnailRevision();
    fetchSlideManifest({ devContainerId, getIdToken });
    return () => setEditorContext({ devContainerId: null, getIdToken: null });
  }, [devContainerId, previewLoaded, getIdToken]);

  // Handler to close project settings drawer
  const handleCloseProjectSettingsDrawer = useCallback(() => {
    setShowProjectSettingsDrawer(false);
  }, []);

  // Handler for mobile tab change (type-safe wrapper)
  const handleMobileActiveTabChange = useCallback(
    (tab: string) => {
      const typedTab = tab as "chat" | "preview" | "visual";
      setMobileActiveTab(typedTab);
      onMobileActiveTabChange?.(typedTab);
    },
    [onMobileActiveTabChange],
  );

  // Check if any drawer is open (to show X icon)
  const isAnyDrawerOpen = showProjectSettingsDrawer;

  // Memoized mobile bottom navigation component
  // Left icon: Clock (history) on chat tab, Palette (themes) on preview/visual tabs
  const MobileBottomNav = useMemo(
    () => (
      <MobileTabSwitcher
        tabs={TABS}
        value={mobileActiveTab}
        onValueChange={handleMobileActiveTabChange}
        showLeftIcon={true}
        showRightIcon={true}
        size={"sm"}
        leftIcon={mobileActiveTab === "chat" ? Clock : Palette}
        rightIcon={isAnyDrawerOpen ? X : undefined}
        onLeftIconClick={() => {
          if (mobileActiveTab === "chat") {
            handleViewModeChange("history");
          } else {
            // Open themes panel on preview/visual tabs
            onActiveMenuItemChange?.("themes");
          }
        }}
        onRightIconClick={() => {
          if (isAnyDrawerOpen) {
            handleCloseProjectSettingsDrawer();
          } else {
            setShowProjectSettingsSheet(true);
          }
        }}
      />
    ),
    [
      mobileActiveTab,
      isAnyDrawerOpen,
      handleMobileActiveTabChange,
      onActiveMenuItemChange,
    ],
  );

  // Handle edit theme from Agent Chat - import theme into theme editor
  const handleEditTheme = useCallback(
    (theme: ThemeContent) => {
      setThemeToEdit(theme);
      setShowThemesList(true);
      onActiveMenuItemChange?.("themes");
    },
    [onActiveMenuItemChange],
  );

  // Open the themes panel without loading a specific theme for editing.
  // Used by ThemePreviewCard's collapsed "applied" state to offer a
  // "Browse themes" link — same destination as handleEditTheme but
  // without the import-for-edit side effect.
  const handleBrowseThemes = useCallback(() => {
    setThemeToEdit(null);
    setShowThemesList(true);
    onActiveMenuItemChange?.("themes");
  }, [onActiveMenuItemChange]);

  // Clear themeToEdit after import completes
  const handleThemeImported = useCallback(() => {
    setThemeToEdit(null);
  }, []);

  // Render themes panel when activeMenuItem is "themes"
  // Note: Allow themes on tablets (use isMobileDevice, not isMobile which includes tablets)
  const renderThemesPanel = () => {
    if (activeMenuItem !== "themes") return null;

    return (
      <LeftSidebarPanel
        isSidebarHidden={isSidebarHidden}
        isMobile={isMobileDevice}
        widthPercent={leftPanelWidth}
      >
        <ThemesListPanel
          onBack={() => {
            setShowThemesList(false);
            onActiveMenuItemChange?.(null); // Clear activeMenuItem to go back to agent view
          }}
          onThemeSelect={(themeId) => {
            console.log("Theme selected:", themeId);
          }}
          previewIframe={previewIframeEl}
          projectId={projectId || ""}
          workspaceId={workspaceId || ""}
          devContainerId={devContainerId}
          themeToEdit={themeToEdit}
          onThemeImported={handleThemeImported}
          onEditTheme={handleEditTheme}
          setToolbarVisible={setToolbarVisible}
        />
      </LeftSidebarPanel>
    );
  };

  // Render left sidebar based on current view mode
  // Note: On mobile/tablet, LeftSidebarPanel returns null - mobile uses the Tabs layout instead
  const renderLeftSide = () => {
    switch (viewMode) {
      case "agent":
        return (
          <LeftSidebarPanel
            isSidebarHidden={isSidebarHidden}
            isMobile={isMobile}
            isFullscreenChat={isFullscreenChat}
            variant="background"
            widthPercent={leftPanelWidth}
          >
            <AgentModeContainer
              ref={agentRef}
              devContainerId={devContainerId}
              projectId={projectId}
              workspaceId={workspaceId}
              previewIframe={previewIframeEl}
              onOpenFile={handleOpenFileInIde}
              onSelectConnections={handleSelectConnections}
              onSelectProjectSettings={handleSelectProjectSettings}
              onSelectImageGenerator={handleSelectImageGenerator}
              onSelectMediaLibrary={handleSelectMediaLibrary}
              onSelectOptisignsAssets={handleSelectOptisignsAssets}
              pendingAssetRefs={pendingAssetRefs}
              onPendingAssetRefsConsumed={() => setPendingAssetRefs([])}
              onAddAssetRefsToComposer={setPendingAssetRefs}
              onSelectThemes={handleSelectThemes}
              onOpenHistory={() => handleViewModeChange("history")}
              fromApp={fromApp}
              builderData={builderData}
              onBuilderDataProcessed={onBuilderDataProcessed}
              onStreamingChange={handleAgentStreamingChange}
              onOpenVisualEditor={() => {
                setViewMode("visual");
                externalOnShowPreviewInAgentViewChange?.(true);
              }}
              mobileActiveTab={mobileActiveTab}
              mode={agentMode}
              model={agentModel}
              onModeChange={setAgentMode}
              onModelChange={setAgentModel}
              onMobileActiveTabChange={handleMobileActiveTabChange}
              onStreamComplete={onStreamComplete}
              isContainerReady={isContainerReady}
              onEditTheme={handleEditTheme}
              onBrowseThemes={handleBrowseThemes}
              onConnectionLinked={onConnectionLinked}
              headerLeftActions={sessionHeaderLeftActions}
              headerRightActions={sessionHeaderRightActions}
            />
          </LeftSidebarPanel>
        );

      case "visual":
        return (
          <LeftSidebarPanel
            isSidebarHidden={isSidebarHidden}
            isMobile={isMobile}
            widthPercent={leftPanelWidth}
          >
            <div
              ref={visualHeaderRef}
              className="px-3 py-2.5 flex items-center gap-1.5 bg-card"
            >
              {sessionHeaderLeftActions}
              <VisualTabsControl
                activeTab={visualEditorTab}
                onChange={setVisualEditorTab}
                compact={
                  visualHeaderWidth > 0 &&
                  visualHeaderWidth < VISUAL_TABS_COMPACT_WIDTH
                }
              />
              {sessionHeaderRightActions}
            </div>
            <div className="flex-1 overflow-y-scroll">
              <VisualEditor
                ref={visualEditorRef}
                devContainerId={devContainerId}
                projectId={projectId}
                previewUrl={previewUrl}
                previewRef={previewIframeRef}
                selectedElement={
                  selectedElements.length > 1 ? null : selectedElement
                }
                multiSelectCount={selectedElements.length}
                isSelectionEnabled={isSelectionEnabled}
                onToggleSelection={handleToggleSelection}
                onElementDeselect={() => {
                  setSelectedElement(null);
                  setSelectedElements([]);
                  setToolbarVisible(false);
                }}
                onCommitStateChange={setCommitState}
                onAiCommand={onAiCommand}
                activeTab={visualEditorTab}
                onActiveTabChange={setVisualEditorTab}
                currentPath={currentPath}
                onPathChange={onPathChange}
              />
            </div>
          </LeftSidebarPanel>
        );

      case "history":
        return (
          <LeftSidebarPanel
            isSidebarHidden={isSidebarHidden}
            isMobile={isMobile}
            widthPercent={leftPanelWidth}
          >
            <CommitHistory
              devContainerId={devContainerId}
              projectId={projectId}
              isStreaming={isStreaming}
              onBack={() => handleViewModeChange("agent")}
            />
          </LeftSidebarPanel>
        );

      case "design":
        return (
          <LeftSidebarPanel
            isSidebarHidden={isSidebarHidden}
            isMobile={isMobile}
            widthPercent={leftPanelWidth}
          >
            <ImageGeneratePanel
              projectId={projectId}
              devContainerId={devContainerId}
              previewUrl={previewUrl}
              onBack={() => handleViewModeChange("visual")}
            />
          </LeftSidebarPanel>
        );

      case "media":
        return (
          <LeftSidebarPanel
            isSidebarHidden={isSidebarHidden}
            isMobile={isMobile}
            widthPercent={leftPanelWidth}
          >
            <MediaLibraryPanel
              devContainerId={devContainerId}
              projectId={projectId}
              previewUrl={previewUrl}
              onBack={() => handleViewModeChange("agent")}
            />
          </LeftSidebarPanel>
        );

      case "optisignsAssets":
        return (
          <LeftSidebarPanel
            isSidebarHidden={isSidebarHidden}
            isMobile={isMobile}
            widthPercent={leftPanelWidth}
          >
            <OptisignsAssetsPanel
              projectId={projectId}
              onSelect={handleOptisignsAssetsSelected}
              onBack={() => handleViewModeChange("agent")}
            />
          </LeftSidebarPanel>
        );

      default:
        return null;
    }
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Error Loading Editor
          </h3>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-purple-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Setting up your environment
          </h3>
          <p className="text-muted-foreground">
            This may take a few moments...
          </p>
        </div>
      </div>
    );
  }

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col h-full bg-background">
        {/* Mobile Layout */}
        {isMobile && (
          <Tabs
            value={mobileActiveTab}
            onValueChange={handleMobileActiveTabChange}
            className="flex flex-col h-full"
          >
            {/* Mobile Content Area */}
            <div className="flex-1 overflow-hidden">
              <TabsContent value="chat" className="h-full m-0">
                <AgentModeContainer
                  ref={agentRef}
                  devContainerId={devContainerId}
                  projectId={projectId}
                  workspaceId={workspaceId}
                  previewIframe={previewIframeEl}
                  onOpenFile={handleOpenFileInIde}
                  onSelectConnections={handleSelectConnections}
                  onSelectProjectSettings={handleSelectProjectSettings}
                  onSelectImageGenerator={handleSelectImageGenerator}
                  onSelectMediaLibrary={handleSelectMediaLibrary}
                  onSelectOptisignsAssets={handleSelectOptisignsAssets}
                  pendingAssetRefs={pendingAssetRefs}
                  onPendingAssetRefsConsumed={() => setPendingAssetRefs([])}
                  onAddAssetRefsToComposer={setPendingAssetRefs}
                  onSelectThemes={handleSelectThemes}
                  onOpenHistory={() => handleViewModeChange("history")}
                  builderData={builderData}
                  onBuilderDataProcessed={onBuilderDataProcessed}
                  onStreamingChange={handleAgentStreamingChange}
                  onOpenVisualEditor={undefined} // Visual editor disabled on mobile
                  mobileActiveTab={mobileActiveTab}
                  onMobileActiveTabChange={handleMobileActiveTabChange}
                  onOpenSettings={() => setShowProjectSettingsSheet(true)}
                  mode={agentMode}
                  model={agentModel}
                  onModeChange={setAgentMode}
                  onModelChange={setAgentModel}
                  onStreamComplete={onStreamComplete}
                  isContainerReady={isContainerReady}
                  onBrowseThemes={handleBrowseThemes}
                  onConnectionLinked={onConnectionLinked}
                  headerLeftActions={sessionHeaderLeftActions}
                  headerRightActions={sessionHeaderRightActions}
                />
              </TabsContent>
              <TabsContent
                value="preview"
                className="h-full bg-white dark:bg-black m-0 relative flex flex-col"
              >
                {/* Preview iframe area */}
                <div className="flex-1 relative overflow-hidden">
                  {!previewLoaded && (
                    <LoadingSpinner message="Loading preview..." />
                  )}
                  <IframeDropZone className="w-full h-full">
                    <iframe
                      ref={setPreviewIframeRef}
                      src={getActualPreviewUrl()}
                      className="w-full h-full border-0"
                      onLoad={handlePreviewLoad}
                      title="Application Preview"
                      allow="screen-wake-lock; fullscreen"
                      sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                    />
                  </IframeDropZone>
                </div>

                {/* Lovable-style bottom bar for preview */}
                <div className="bg-card border-t border-border pb-[env(safe-area-inset-bottom)]">
                  {/* Top row: route path + action icons */}
                  <div className="flex items-center justify-between px-4 py-2 border-b border-border">
                    {/* Route selector */}
                    <div className="relative">
                      <button
                        onClick={onTogglePathDropdown}
                        className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-muted transition-colors text-muted-foreground"
                      >
                        <span className="text-sm font-medium">
                          {currentPath || "/"}
                        </span>
                        <ChevronRight className="w-3 h-3 rotate-90" />
                      </button>
                      {/* Path dropdown */}
                      {showPathDropdown &&
                        availablePaths &&
                        availablePaths.length > 0 && (
                          <>
                            <div
                              className="fixed inset-0 z-40"
                              onClick={onTogglePathDropdown}
                            />
                            <div className="absolute bottom-full left-0 mb-2 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 min-w-[160px] max-h-[200px] overflow-y-auto">
                              {availablePaths.map((page) => (
                                <button
                                  key={page.route}
                                  onClick={() => {
                                    onPathSelect?.(page.route);
                                    onTogglePathDropdown?.();
                                  }}
                                  className={cn(
                                    "w-full px-3 py-2 text-sm text-left hover:bg-accent transition-colors",
                                    currentPath === page.route && "bg-accent",
                                  )}
                                >
                                  {page.route}
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                    </div>

                    {/* Action icons */}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={
                          externalOnOpenPreviewInNewWindow ||
                          handleOpenPreviewInNewWindow
                        }
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                        title="Open in new tab"
                      >
                        <ExternalLink className="w-4 h-4 text-muted-foreground" />
                      </button>
                      <button
                        onClick={externalOnReloadPreview || handleReloadPreview}
                        className="p-2 rounded-lg hover:bg-muted transition-colors"
                        title="Reload preview"
                      >
                        <RefreshCw className="w-4 h-4 text-muted-foreground" />
                      </button>
                    </div>
                  </div>

                  {/* Bottom row: left icon | Chat/Preview tabs | three dots */}
                  <div className="flex items-center gap-2 px-4 py-3">
                    {/* Left icon: Clock (history) on chat tab, Palette (themes) on preview tab */}
                    <button
                      onClick={() => {
                        if (mobileActiveTab === "chat") {
                          handleViewModeChange("history");
                        } else {
                          onActiveMenuItemChange?.("themes");
                        }
                      }}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-muted hover:bg-accent transition-colors flex-shrink-0"
                      title={
                        mobileActiveTab === "chat"
                          ? "Version history"
                          : "Themes"
                      }
                    >
                      {mobileActiveTab === "chat" ? (
                        <Clock className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <Palette className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>

                    {/* Chat/Preview segmented control */}
                    <div className="flex-1 flex items-center bg-muted rounded-xl p-1">
                      <button
                        onClick={() => handleMobileActiveTabChange("chat")}
                        className={cn(
                          "flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors",
                          mobileActiveTab === "chat"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        Chat
                      </button>
                      <button
                        onClick={() => handleMobileActiveTabChange("preview")}
                        className={cn(
                          "flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors",
                          mobileActiveTab === "preview"
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground",
                        )}
                      >
                        Preview
                      </button>
                    </div>

                    {/* More options button */}
                    <button
                      onClick={() => setShowProjectSettingsSheet(true)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-muted hover:bg-accent transition-colors flex-shrink-0"
                      title="More options"
                    >
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              </TabsContent>
            </div>
          </Tabs>
        )}

        {/* Desktop Layout */}
        {!isMobile && (
          <>
            {/* Toggle Bar - Only show when viewMode is not controlled externally */}
            {!externalViewMode && (
              <div className="flex items-center justify-between px-4 py-2 bg-card border-b border-border">
                <div className="flex items-center gap-3">
                  <Button
                    variant={viewMode === "agent" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleViewModeChange("agent")}
                    className={cn(
                      "gap-2",
                      viewMode === "agent"
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "",
                    )}
                  >
                    <Bot className="w-4 h-4" />
                    Agent
                  </Button>
                  <Button
                    variant={viewMode === "visual" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleViewModeChange("visual")}
                    disabled={!canEditFiles}
                    className={cn(
                      "gap-2",
                      viewMode === "visual"
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "",
                    )}
                  >
                    <Palette className="w-4 h-4" />
                    Visual Editor
                  </Button>
                  <Button
                    variant={viewMode === "code" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleViewModeChange("code")}
                    disabled={!canEditFiles || !workspaceUrl}
                    className={cn(
                      "gap-2",
                      viewMode === "code"
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "",
                    )}
                  >
                    <Code className="w-4 h-4" />
                    Code
                  </Button>
                  <Button
                    variant={viewMode === "history" ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleViewModeChange("history")}
                    className={cn(
                      "gap-2",
                      viewMode === "history"
                        ? "bg-purple-600 hover:bg-purple-700"
                        : "",
                    )}
                  >
                    <Clock className="w-4 h-4" />
                  </Button>
                </div>
                <PublishDropdown
                  projectId={projectId}
                  onNavigateToSettings={(section) => {
                    handleViewModeChange("settings");
                    onSettingsSectionChange?.(section);
                  }}
                />
              </div>
            )}
            {/* Settings View */}
            {viewMode === "settings" && projectId && (
              <ProjectSettings
                key={initialSection}
                projectId={projectId}
                projectName={projectName}
                initialSection={initialSection}
                onSectionChange={onSettingsSectionChange}
                onClose={() => handleViewModeChange("agent")}
              />
            )}
            {/* Main Content Area */}
            <div
              ref={containerRef}
              className={cn(
                "flex flex-1 relative min-h-0",
                viewMode === "settings" && "hidden",
              )}
            >
              {/* Transparent overlay during resize - keeps drag smooth over iframes */}
              {(isResizing || isResizingLeft) && (
                <div className="fixed inset-0 z-[60] cursor-col-resize" />
              )}
              {/* Left Sidebar - renders agent/visual/history based on viewMode, or themes when activeMenuItem is "themes" AND viewMode is "agent" */}
              {activeMenuItem === "themes" &&
              (viewMode === "agent" || viewMode === "visual")
                ? renderThemesPanel()
                : renderLeftSide()}

              {/* Resize Handle between left sidebar (chat/visual/etc) and right preview */}
              {(viewMode === "agent" ||
                viewMode === "visual" ||
                viewMode === "history" ||
                viewMode === "design" ||
                viewMode === "media" ||
                viewMode === "optisignsAssets") &&
                !isMobile &&
                !isSidebarHidden &&
                !(viewMode === "agent" && isFullscreenChat) && (
                  <div
                    className="w-1 shrink-0 bg-border hover:bg-purple-500 cursor-col-resize transition-colors relative group z-30"
                    onMouseDown={handleLeftMouseDown}
                  >
                    {/* Wider invisible hit area - keeps the divider grabbable even when a side is fully collapsed against the edge */}
                    <div className="absolute inset-y-0 -left-2 -right-2 cursor-col-resize" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center pointer-events-none">
                      <div className="w-0.5 h-6 bg-muted-foreground/40 group-hover:bg-purple-400 transition-colors" />
                    </div>
                  </div>
                )}

              {/* Code and Preview Area */}
              <div
                className={cn(
                  "flex flex-1",
                  viewMode === "code" && "flex",
                  (viewMode === "agent" ||
                    viewMode === "visual" ||
                    viewMode === "history" ||
                    viewMode === "design" ||
                    viewMode === "media" ||
                    viewMode === "optisignsAssets") &&
                    "hidden",
                )}
              >
                {/* IDE Panel */}
                <div
                  className={`relative ${isFullscreenPreview ? "hidden" : ""} ${
                    isFullscreenIde ? "w-full" : ""
                  }`}
                  style={{
                    width: isFullscreenIde ? "100%" : `${splitPosition}%`,
                  }}
                >
                  {!externalOnReloadPreview && (
                    <div className="absolute top-0 left-0 right-0 bg-card border-b border-border px-4 py-2 flex items-center justify-between z-10">
                      <div className="flex items-center gap-2">
                        <Code className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex items-center gap-1">
                        <Hint
                          delayDuration={0}
                          text="Open in new window"
                          side="bottom"
                          align="center"
                        >
                          <button
                            onClick={handleOpenIdeInNewWindow}
                            className="p-1 hover:bg-accent rounded transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                          </button>
                        </Hint>
                        <Hint
                          delayDuration={0}
                          text={
                            isFullscreenIde
                              ? "Exit fullscreen"
                              : "Enter fullscreen"
                          }
                          side="bottom"
                          align="center"
                        >
                          <button
                            onClick={handleToggleFullscreenIde}
                            className="p-1 hover:bg-accent rounded transition-colors"
                          >
                            {isFullscreenIde ? (
                              <Minimize2 className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                            ) : (
                              <Maximize2 className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                            )}
                          </button>
                        </Hint>
                      </div>
                    </div>
                  )}
                  <div
                    className={cn(
                      "h-full relative",
                      !externalOnReloadPreview && "pt-10",
                    )}
                  >
                    {/* Top-left icon indicator for IDE */}
                    {externalOnReloadPreview && (
                      <div className="absolute top-4 left-4 z-50 flex items-center gap-2 bg-black/30 backdrop-blur-sm border border-white/30 rounded-lg px-2.5 py-2 shadow-lg">
                        <Code className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white">
                          Code
                        </span>
                      </div>
                    )}
                    {!canEditFiles ? (
                      <div className="w-full h-full flex items-center justify-center bg-muted">
                        <div className="text-center">
                          <Eye className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                          <p className="text-muted-foreground">
                            View only - code editing is disabled
                          </p>
                        </div>
                      </div>
                    ) : (
                      <>
                        {!ideLoaded && (
                          <LoadingSpinner message="Loading IDE..." />
                        )}
                        <iframe
                          ref={ideIframeRef}
                          src={getActualIdeUrl()}
                          className="w-full h-full border-0"
                          onLoad={handleIdeLoad}
                          title="Code Editor"
                          allow="clipboard-read; clipboard-write; fullscreen"
                          sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
                        />
                      </>
                    )}
                  </div>
                </div>

                {/* Resize Handle between IDE and Preview */}
                {!isFullscreenIde &&
                  !isFullscreenPreview &&
                  viewMode !== "agent" &&
                  viewMode !== "visual" && (
                    <div
                      className="w-1 bg-gray-700 hover:bg-purple-500 cursor-col-resize transition-colors relative group"
                      onMouseDown={handleMouseDown}
                    >
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center">
                        <div className="w-0.5 h-6 bg-gray-500 group-hover:bg-purple-400 transition-colors" />
                      </div>
                    </div>
                  )}

                {/* Preview Panel */}
                <div
                  className={cn(
                    "relative",
                    isFullscreenIde && "hidden",
                    isFullscreenPreview ? "w-full" : "flex-1",
                    (viewMode === "agent" || viewMode === "visual") && "hidden",
                  )}
                >
                  {!externalOnReloadPreview && (
                    <div className="absolute top-0 left-0 right-0 bg-card border-b border-border px-4 py-2 flex items-center justify-between z-10">
                      <div className="flex items-center gap-2">
                        <Eye className="w-4 h-4 text-muted-foreground" />
                      </div>
                      <div className="flex items-center gap-1">
                        <Hint
                          delayDuration={0}
                          text="Open in new window"
                          side="bottom"
                          align="start"
                        >
                          <button
                            onClick={handleOpenPreviewInNewWindow}
                            className="p-1 hover:bg-accent rounded transition-colors"
                            title="Open in new window"
                          >
                            <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                          </button>
                        </Hint>
                        <Hint
                          delayDuration={0}
                          text={
                            isFullscreenPreview
                              ? "Exit fullscreen"
                              : "Enter fullscreen"
                          }
                          side="bottom"
                          align="center"
                        >
                          <button
                            onClick={handleToggleFullscreenPreview}
                            className="p-1 hover:bg-accent rounded transition-colors"
                          >
                            {isFullscreenPreview ? (
                              <Minimize2 className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                            ) : (
                              <Maximize2 className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                            )}
                          </button>
                        </Hint>
                      </div>
                    </div>
                  )}
                  <div
                    className={cn(
                      "h-full bg-white relative",
                      !externalOnReloadPreview && "pt-10",
                    )}
                  >
                    {/* Top-left icon indicator for split mode */}
                    {externalOnReloadPreview && (
                      <div className="absolute top-4 left-4 z-50 flex items-center gap-2 bg-black/30 backdrop-blur-sm border border-white/30 rounded-lg px-2.5 py-2 shadow-lg">
                        <Eye className="w-4 h-4 text-white" />
                        <span className="text-xs font-semibold text-white">
                          Preview
                        </span>
                      </div>
                    )}
                    {/* Floating pill for split mode - only show when external handlers are provided */}
                    {externalOnReloadPreview && (
                      <div className="absolute top-4 right-4 z-50 flex items-center gap-1 bg-black/30 backdrop-blur-sm border border-white/30 rounded-lg px-2 py-1 shadow-lg">
                        <Hint
                          delayDuration={0}
                          text="Open in new tab"
                          side="bottom"
                          align="center"
                        >
                          <button
                            onClick={handleOpenPreviewInNewWindow}
                            className="p-1.5 hover:bg-white/15 rounded transition-colors"
                          >
                            <ExternalLink className="w-4 h-4 text-white" />
                          </button>
                        </Hint>
                        <Hint
                          delayDuration={0}
                          text="Reload preview"
                          side="bottom"
                          align="center"
                        >
                          <button
                            onClick={handleReloadPreview}
                            className="p-1.5 hover:bg-white/15 rounded transition-colors"
                          >
                            <RefreshCw className="w-4 h-4 text-white" />
                          </button>
                        </Hint>
                      </div>
                    )}
                    {(!previewLoaded || isPreviewLoading) && (
                      <LoadingSpinner
                        message="Loading preview..."
                        bgColor="bg-white"
                        showSlideshow={false}
                      />
                    )}
                    <IframeDropZone className="w-full h-full">
                      <iframe
                        ref={setPreviewIframeRef}
                        src={getActualPreviewUrl()}
                        className="w-full h-full border-0"
                        onLoad={handlePreviewLoad}
                        title="Application Preview"
                        allow="screen-wake-lock; fullscreen"
                        sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                      />
                    </IframeDropZone>
                  </div>
                </div>
              </div>

              {/* Preview Panel (71% right when in agent, visual, history, design, or media view) - hidden on mobile or fullscreen chat */}
              {(viewMode === "agent" ||
                viewMode === "visual" ||
                viewMode === "history" ||
                viewMode === "design" ||
                viewMode === "media" ||
                viewMode === "optisignsAssets") &&
                !isMobile &&
                !(viewMode === "agent" && isFullscreenChat) && (
                  <div
                    className={cn(
                      "relative bg-white flex h-full overflow-hidden",
                      isSidebarHidden || isMobile ? "w-full" : "flex-1 min-w-0",
                    )}
                  >
                    {/* Slides Sidebar - only for slide projects */}
                    {isSlideProject && (
                      <SlidesPanel
                        previewIframeRef={previewIframeRef}
                        previewUrl={previewUrl}
                        projectId={projectId}
                        devContainerId={devContainerId}
                        getIdToken={getIdToken}
                      />
                    )}
                    <div className="relative flex-1 min-w-0">
                      {!externalOnReloadPreview && (
                        <div className="absolute top-0 left-0 right-0 bg-card border-b border-border px-4 py-2 flex items-center justify-between z-10">
                          {/* Left: Slide Export & Present */}
                          <div className="flex items-center gap-1">
                            {isSlideProject && (
                              <>
                                <SlideExportButton
                                  previewIframeRef={previewIframeRef}
                                  isExporting={isExporting}
                                  projectId={projectId || ""}
                                  getIdToken={getIdToken}
                                />
                                <div className="flex items-stretch">
                                  <Hint
                                    delayDuration={0}
                                    text="Present fullscreen"
                                    side="bottom"
                                    align="center"
                                  >
                                    <button
                                      onClick={() => {
                                        if (!projectId) return;
                                        // Open the in-page Present overlay
                                        // AND request browser fullscreen on
                                        // its root. The overlay already
                                        // hosts the annotation layer as a
                                        // descendant, so fullscreening it
                                        // (instead of the bare iframe)
                                        // keeps the annotation toolbar
                                        // + strokes visible inside browser
                                        // fullscreen. Also sidesteps the
                                        // old issue where the editor
                                        // iframe's inline transform fought
                                        // the `:fullscreen` UA rule and
                                        // rendered the slide tiny.
                                        useSlideStore
                                          .getState()
                                          .setCurrentSlide(0);
                                        setPresentInitialUrl(
                                          buildPresentSlideUrl(0),
                                        );
                                        setPresentRequestFullscreen(true);
                                        setIsPresentMode(true);
                                      }}
                                      className="flex items-center gap-1.5 px-2 py-1 text-xs font-medium rounded-l rounded-r-none hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                                    >
                                      <Presentation className="w-3.5 h-3.5" />
                                      Present
                                    </button>
                                  </Hint>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <button
                                        aria-label="Present options"
                                        className="flex items-center px-1 py-1 rounded-r rounded-l-none border-l border-border/40 hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                                      >
                                        <ChevronDown className="w-3 h-3" />
                                      </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem
                                        onClick={() => {
                                          if (!projectId) return;

                                          // 1. Editor tab enters Present mode — in-page
                                          //    overlay (fixed inset-0 z-[9999]), NOT the
                                          //    Fullscreen API. Keeps browser chrome
                                          //    visible so the user can switch tabs.
                                          //    Reset to slide 0 and capture the initial
                                          //    iframe URL once (subsequent nav uses
                                          //    postMessage so the iframe doesn't reload).
                                          useSlideStore
                                            .getState()
                                            .setCurrentSlide(0);
                                          setPresentInitialUrl(
                                            buildPresentSlideUrl(0),
                                          );
                                          setIsPresentMode(true);

                                          // 2. Open the presenter window. Bulletproof
                                          //    handoff via base64 query param.
                                          const presenterUrl = buildPresenterUrl(
                                            projectId,
                                            { previewUrl, devContainerId },
                                          );
                                          window.open(
                                            presenterUrl,
                                            "optidev-slide-presenter",
                                            "popup=yes,width=1280,height=800",
                                          );
                                        }}
                                      >
                                        <Presentation className="w-4 h-4 mr-2" />
                                        Presenter view
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                                {isSlidesCollapsed && (
                                  <Hint
                                    delayDuration={0}
                                    text="Show slides panel"
                                    side="bottom"
                                    align="center"
                                  >
                                    <button
                                      onClick={() => setSlidesCollapsed(false)}
                                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                                    >
                                      <PanelLeft className="w-3.5 h-3.5" />
                                    </button>
                                  </Hint>
                                )}
                              </>
                            )}
                          </div>
                          {/* Right: Reload, Expand, Open */}
                          <div className="flex items-center gap-1">
                            <Hint
                              delayDuration={0}
                              text="Reload preview"
                              side="bottom"
                              align="center"
                            >
                              <button
                                onClick={handleReloadPreview}
                                className="p-1 hover:bg-accent rounded transition-colors"
                              >
                                <RefreshCw className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                              </button>
                            </Hint>
                            <Hint
                              delayDuration={0}
                              text="Open in new window"
                              side="bottom"
                              align="start"
                            >
                              <button
                                onClick={
                                  showIdeInAgentView
                                    ? handleOpenIdeInNewWindow
                                    : handleOpenPreviewInNewWindow
                                }
                                className="p-1 hover:bg-accent rounded transition-colors"
                              >
                                <ExternalLink className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                              </button>
                            </Hint>
                          </div>
                        </div>
                      )}
                      {/* Visual Editor Floating Controls - only show in visual mode when there are changes */}
                      {viewMode === "visual" &&
                        visualEditorFloatingControls && (
                          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-20">
                            {visualEditorFloatingControls}
                          </div>
                        )}
                      <div
                        className={cn(
                          "h-full relative bg-background dotted-background-interactive",
                          !externalOnReloadPreview && "pt-10",
                        )}
                      >
                        {showIdeInAgentView ? (
                          // Show IDE iframe (or view-only message for viewers)
                          !canEditFiles ? (
                            <div className="w-full h-full flex items-center justify-center bg-muted">
                              <div className="text-center">
                                <Eye className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                                <p className="text-muted-foreground">
                                  View only - code editing is disabled
                                </p>
                              </div>
                            </div>
                          ) : (
                            <>
                              {!ideLoaded && (
                                <LoadingSpinner message="Loading IDE..." />
                              )}
                              <iframe
                                ref={ideIframeRef}
                                src={getActualIdeUrl()}
                                className="w-full h-full border-0 bg-background"
                                onLoad={handleIdeLoad}
                                title="Code Editor"
                                allow="clipboard-read; clipboard-write; fullscreen"
                                sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-downloads"
                              />
                            </>
                          )
                        ) : (
                          // Show Preview iframe
                          <div className="flex flex-col h-full relative flex-1 min-w-0">
                            {/* Slide Export & Present bar - directly above iframe */}
                            {isSlideProject && (
                              <div className="flex items-center justify-between px-3 py-1.5 bg-card border-b border-border flex-shrink-0">
                                {/* Left: Slides toggle */}
                                <div className="flex items-center gap-1">
                                  {isSlidesCollapsed && (
                                    <Hint
                                      delayDuration={0}
                                      text="Show slides panel"
                                      side="bottom"
                                      align="center"
                                    >
                                      <button
                                        onClick={() =>
                                          setSlidesCollapsed(false)
                                        }
                                        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
                                      >
                                        <PanelLeft className="w-3.5 h-3.5" />
                                      </button>
                                    </Hint>
                                  )}
                                </div>
                                {/* Right: Export & Present */}
                                <div className="flex items-center gap-1.5">
                                  <SlideExportButton
                                    previewIframeRef={previewIframeRef}
                                    isExporting={isExporting}
                                    projectId={projectId || ""}
                                    getIdToken={getIdToken}
                                  />
                                  <div className="flex items-stretch">
                                    <button
                                      onClick={() => {
                                        if (!projectId) return;
                                        // Open the in-page Present overlay
                                        // AND request browser fullscreen on
                                        // its root. The overlay already
                                        // hosts the annotation layer as a
                                        // descendant, so fullscreening it
                                        // (instead of the bare iframe)
                                        // keeps the annotation toolbar
                                        // + strokes visible inside browser
                                        // fullscreen. Also sidesteps the
                                        // old issue where the editor
                                        // iframe's inline transform fought
                                        // the `:fullscreen` UA rule and
                                        // rendered the slide tiny.
                                        useSlideStore
                                          .getState()
                                          .setCurrentSlide(0);
                                        setPresentInitialUrl(
                                          buildPresentSlideUrl(0),
                                        );
                                        setPresentRequestFullscreen(true);
                                        setIsPresentMode(true);
                                      }}
                                      className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-l-md rounded-r-none border border-r-0 border-border hover:bg-accent transition-colors text-foreground"
                                    >
                                      <Presentation className="w-3.5 h-3.5" />
                                      Present
                                    </button>
                                    <DropdownMenu>
                                      <DropdownMenuTrigger asChild>
                                        <button
                                          aria-label="Present options"
                                          className="flex items-center px-1.5 py-1 rounded-r-md rounded-l-none border border-border hover:bg-accent transition-colors text-foreground"
                                        >
                                          <ChevronDown className="w-3 h-3" />
                                        </button>
                                      </DropdownMenuTrigger>
                                      <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                          onClick={() => {
                                          if (!projectId) return;

                                          // 1. Editor tab enters Present mode — in-page
                                          //    overlay (fixed inset-0 z-[9999]), NOT the
                                          //    Fullscreen API. Keeps browser chrome
                                          //    visible so the user can switch tabs.
                                          //    Reset to slide 0 and capture the initial
                                          //    iframe URL once (subsequent nav uses
                                          //    postMessage so the iframe doesn't reload).
                                          useSlideStore
                                            .getState()
                                            .setCurrentSlide(0);
                                          setPresentInitialUrl(
                                            buildPresentSlideUrl(0),
                                          );
                                          setIsPresentMode(true);

                                          // 2. Open the presenter window. Bulletproof
                                          //    handoff via base64 query param.
                                          const presenterUrl = buildPresenterUrl(
                                            projectId,
                                            { previewUrl, devContainerId },
                                          );
                                          window.open(
                                            presenterUrl,
                                            "optidev-slide-presenter",
                                            "popup=yes,width=1280,height=800",
                                          );
                                        }}
                                        >
                                          <Presentation className="w-4 h-4 mr-2" />
                                          Presenter view
                                        </DropdownMenuItem>
                                      </DropdownMenuContent>
                                    </DropdownMenu>
                                  </div>
                                </div>
                              </div>
                            )}
                            {(!previewLoaded || isPreviewLoading) && (
                              <LoadingSpinner
                                message="Loading preview..."
                                bgColor="bg-white"
                                showSlideshow={true}
                              />
                            )}
                            {/* Tour anchor for the preview step.
                                Deliberately this element and not one of its
                                children: it is the app surface exactly (no
                                Present/Export bar above it), and it sits above
                                the isResponsiveMode branch so it exists in both
                                responsive and plain modes. Anchoring inside that
                                branch is how the preview step went missing. */}
                            <div
                              data-tour-anchor="preview"
                              className="flex-1 relative flex flex-col min-h-0"
                            >
                              <div className="flex-1 min-h-0 relative">
                              <TransformWrapper
                                key={transformKey}
                                initialScale={1}
                                minScale={0.1}
                                maxScale={5}
                                centerOnInit={true}
                                wheel={{
                                  step: 0.1,
                                  wheelDisabled: false,
                                  touchPadDisabled: false,
                                  activationKeys: ["Meta", "Control"],
                                }}
                                panning={{
                                  disabled: isPanningDisabled,
                                  velocityDisabled: false,
                                  lockAxisX: false,
                                  lockAxisY: false,
                                }}
                                doubleClick={{ disabled: true }}
                                pinch={{ disabled: true }}
                                limitToBounds={false}
                                centerZoomedOut={false}
                                disablePadding={false}
                                smooth={true}
                                alignmentAnimation={{
                                  disabled: false,
                                  sizeX: 100,
                                  sizeY: 100,
                                  animationTime: 200,
                                  animationType: "easeOut",
                                }}
                              >
                                {({ resetTransform, zoomIn, zoomOut }) => {
                                  // Expose transform controls to parent
                                  if (onTransformRef) {
                                    onTransformRef({
                                      zoomIn,
                                      zoomOut,
                                      resetTransform,
                                    });
                                  }

                                  return (
                                    <TransformComponent
                                      wrapperStyle={{
                                        width: "100%",
                                        height: "100%",
                                        cursor: "grab",
                                      }}
                                      contentStyle={{
                                        width: "100%",
                                        height: "100%",
                                      }}
                                    >
                                      {isResponsiveMode ? (
                                        <div className="relative w-full h-full">
                                          {(!previewLoaded ||
                                            isPreviewLoading) &&
                                            !isDevelopment &&
                                            !isShowingStaticPreview && (
                                              <div className="absolute inset-0 flex items-center justify-center bg-white dark:bg-gray-900 z-50">
                                                <LoadingSpinner
                                                  message="Loading preview..."
                                                  bgColor="bg-transparent"
                                                  showSlideshow={true}
                                                />
                                              </div>
                                            )}
                                          <ResponsiveIframeViewer
                                            src={getActualPreviewUrl()}
                                            onIframeLoad={handlePreviewLoad}
                                            iframeRef={setPreviewIframeRef}
                                            title="Application Preview"
                                            size={
                                              isCustomViewport
                                                ? undefined
                                                : viewportSize ||
                                                  ViewportSize.desktop
                                            }
                                            width={
                                              isCustomViewport
                                                ? customWidth
                                                : undefined
                                            }
                                            height={
                                              isCustomViewport
                                                ? customHeight
                                                : undefined
                                            }
                                            allowResizingY={
                                              viewMode === "visual"
                                            }
                                            allowResizingX={
                                              viewMode === "visual"
                                            }
                                            showControls={false}
                                            onFluidSelect={handleFluidSelect}
                                            onResizeStart={handleResizeStart}
                                            onResizeStop={handleResizeStop}
                                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                                            allow="screen-wake-lock; fullscreen"
                                          />
                                        </div>
                                      ) : (
                                        <IframeDropZone className="w-full h-full">
                                          <iframe
                                            ref={setPreviewIframeRef}
                                            src={getActualPreviewUrl()}
                                            className="w-full h-full border-0"
                                            onLoad={handlePreviewLoad}
                                            title="Application Preview"
                                            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
                                          />
                                        </IframeDropZone>
                                      )}
                                    </TransformComponent>
                                  );
                                }}
                              </TransformWrapper>
                              </div>
                              {/* Speaker notes editor — sits OUTSIDE the
                                  TransformWrapper so its inputs receive
                                  clicks (the wrapper captures pointer
                                  events for pan/zoom). `shrink-0` keeps
                                  it visible even when the iframe is
                                  taller (4:3 viewport). */}
                              {isSlideProject &&
                                slidesForNotes[
                                  currentSlideIndexForNotes
                                ] && (
                                  <div className="shrink-0">
                                    <SlideNotesEditor
                                      slide={
                                        slidesForNotes[
                                          currentSlideIndexForNotes
                                        ]
                                      }
                                      devContainerId={devContainerId}
                                      getIdToken={getIdToken}
                                    />
                                  </div>
                                )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
            </div>
          </>
        )}
        {/* Connections Modal */}
        {projectId && (
          <ProjectConnectionsModal
            isOpen={showConnectionsModal}
            onClose={() => setShowConnectionsModal(false)}
            projectId={projectId}
          />
        )}

        {/* Commit History Sheet */}
        {isMobile && (
          <CommitHistorySheet
            open={viewMode === "history"}
            onOpenChange={(open) => {
              if (!open) {
                handleViewModeChange("agent");
              }
            }}
            devContainerId={devContainerId}
            projectId={projectId}
            isStreaming={false}
          />
        )}

        {/* Project Settings Menu Sheet */}
        {isMobile && (
          <ProjectSettingsSheet
            open={showProjectSettingsSheet}
            onOpenChange={setShowProjectSettingsSheet}
            onOptionSelect={(section) => {
              setProjectSettingsSection(section);
              setShowProjectSettingsDrawer(true);
              setShowProjectSettingsSheet(false);
            }}
            onVersionHistoryClick={() => {
              handleViewModeChange("history");
            }}
          />
        )}

        {/* Project Settings Drawer (shows full ProjectSettings component) */}
        {isMobile && (
          <ProjectSettingsDrawerSheet
            open={showProjectSettingsDrawer}
            onOpenChange={(open) => {
              if (!open) {
                handleCloseProjectSettingsDrawer();
              }
            }}
            projectId={projectId || ""}
            projectName={projectName || "Project"}
            initialSection={projectSettingsSection}
            mode={agentMode}
            model={agentModel}
            onModeChange={setAgentMode}
            onModelChange={setAgentModel}
            isStreaming={isAgentStreaming}
          />
        )}

        {/* Image Generator Sheet */}
        {isMobile && (
          <BottomSheet
            open={viewMode === "design"}
            onOpenChange={(open) => {
              if (!open) {
                handleViewModeChange("agent");
              }
            }}
            hideTitle={true}
            hideCloseButton={true}
          >
            <ImageGeneratePanel
              projectId={projectId}
              devContainerId={devContainerId}
              previewUrl={previewUrl}
              onBack={() => handleViewModeChange("visual")}
            />
          </BottomSheet>
        )}

        {/* Media Search Sheet */}
        {isMobile && (
          <BottomSheet
            open={viewMode === "media"}
            onOpenChange={(open) => {
              if (!open) {
                handleViewModeChange("agent");
              }
            }}
            hideTitle={true}
            hideCloseButton={true}
          >
            <MediaLibraryPanel
              devContainerId={devContainerId}
              projectId={projectId}
              previewUrl={previewUrl}
              onBack={() => handleViewModeChange("agent")}
            />
          </BottomSheet>
        )}

        {/* OptiSigns Assets Sheet */}
        {isMobile && (
          <BottomSheet
            open={viewMode === "optisignsAssets"}
            onOpenChange={(open) => {
              if (!open) {
                handleViewModeChange("agent");
              }
            }}
            hideTitle={true}
            hideCloseButton={true}
          >
            <OptisignsAssetsPanel
              projectId={projectId}
              onSelect={handleOptisignsAssetsSelected}
              onBack={() => handleViewModeChange("agent")}
            />
          </BottomSheet>
        )}

        {/* Themes Sheet (Mobile) */}
        {isMobile && (
          <BottomSheet
            open={activeMenuItem === "themes"}
            onOpenChange={(open) => {
              if (!open) {
                onActiveMenuItemChange?.(null);
              }
            }}
            title="Themes"
          >
            <ThemesListPanel
              onBack={() => {
                setShowThemesList(false);
                onActiveMenuItemChange?.(null);
              }}
              onThemeSelect={(themeId) => {
                console.log("Theme selected:", themeId);
              }}
              previewIframe={previewIframeEl}
              projectId={projectId || ""}
              workspaceId={workspaceId || ""}
              devContainerId={devContainerId}
              themeToEdit={themeToEdit}
              onThemeImported={handleThemeImported}
              onEditTheme={handleEditTheme}
              setToolbarVisible={setToolbarVisible}
            />
          </BottomSheet>
        )}

        <DragOverlay dropAnimation={null}>
          {activeItem ? (
            <div className="flex flex-col items-center justify-center p-3 bg-purple-600 text-white rounded-md text-xs opacity-80 transform rotate-1 cursor-grabbing">
              {activeItem.icon && <activeItem.icon className="w-5 h-5 mb-1" />}
              <span>{activeItem.name || "Component"}</span>
            </div>
          ) : null}
        </DragOverlay>
      </div>
      <FloatingToolbar
        bounds={toolbarBounds}
        visible={
          toolbarVisible &&
          viewMode === "visual" &&
          !showIdeInAgentView &&
          !showIconPicker &&
          !showImagePicker
        }
        onAICommand={onAiCommand}
        onSelectParent={onSelectParent}
        onBringToFront={handleBringToFront}
        onBringForward={handleBringForward}
        onSendBackward={handleSendBackward}
        onSendToBack={handleSendToBack}
        onDuplicate={
          isDataBoundElement(selectedElement) ? undefined : handleDuplicate
        }
        iframeRef={previewIframeRef}
      />
      <IconPickerPopup
        open={showIconPicker}
        onSelect={handleIconSelected}
        onClose={handleIconPickerClose}
      />
      {/* Mounted on first open and kept mounted after that — the popup
          returns null when `open` is false, but its hooks stay alive so
          generated images and project assets don't refetch on every reopen. */}
      {hasOpenedMediaPicker && (
        <MediaPickerPopup
          open={showImagePicker}
          mediaType={
            pendingImageDrop?.componentType === "video"
              ? "video"
              : pendingImageDrop?.componentType === "audio"
                ? "audio"
                : "image"
          }
          onSelect={handleImageSelected}
          onSkip={handleImageSkip}
          onClose={handleImagePickerClose}
          projectId={projectId}
          devContainerId={devContainerId}
          previewUrl={previewUrl}
        />
      )}
      {isPresentMode &&
        presentInitialUrl &&
        // Portal to document.body so the overlay escapes any ancestor
        // stacking context (DashboardLayout's sidebar parent has CSS that
        // creates a containing block, trapping `fixed inset-0` inside it
        // and leaving the sidebar visible). Body-level portal guarantees
        // true viewport coverage.
        createPortal(
          <div
            ref={presentOverlayRootRef}
            className="fixed inset-0 z-[9999] bg-black flex items-center justify-center"
          >
            {/* Letterbox the iframe to the deck's aspectRatio. Belt-and-
                braces with the template's own letterbox: existing projects
                whose bundled SlideRenderer predates that change still get
                proper bars here. min(100vw, 100vh*ratio) picks the
                limiting axis so 16:9 decks letterbox vertically when the
                browser window is taller than 16:9, and 4:3 decks
                pillarbox horizontally when the window is widescreen. */}
            <div
              className="relative bg-black overflow-hidden"
              style={{
                width: `min(100vw, calc(100vh * ${presentAspectRatio === "4:3" ? "(4 / 3)" : "(16 / 9)"}))`,
                aspectRatio:
                  presentAspectRatio === "4:3" ? "4 / 3" : "16 / 9",
              }}
            >
              {/* Render at the deck's logical 1920×1080 (or 1440×1080 for
                  4:3) and scale to fit — keeps slide CSS viewport units
                  resolving to design dimensions so content can't get
                  clipped by a smaller host pane. */}
              <ScaledSlideIframe
                ref={presentOverlayIframeRef}
                src={presentInitialUrl}
                aspectRatio={
                  presentAspectRatio === "4:3" ? "4:3" : "16:9"
                }
                title="Presentation"
                className="absolute inset-0"
                allow="fullscreen"
                sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              />
            </div>
            {/* Auto-hiding close affordance — invisible 48px hover zone at
                the top. Moving the mouse to the top of the screen fades
                the X in; moving away fades it out. The cross-origin iframe
                eats keyboard focus, so the X is the guaranteed exit. Esc
                still works when the parent has focus. */}
            <div className="group absolute top-0 left-0 right-0 h-12">
              <button
                onClick={() => setIsPresentMode(false)}
                title="Exit presentation"
                aria-label="Exit presentation"
                className="absolute top-3 right-3 p-1.5 text-white/70 hover:text-white transition-opacity opacity-0 group-hover:opacity-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Display-only annotation layer. Renders strokes received
                from the presenter window via BC; no toolbar (presenter
                is the sole input source). active={isPresentMode} so
                exiting Present mode wipes the in-memory annotations. */}
            <SlideAnnotationLayer
              iframeRef={presentOverlayIframeRef}
              projectId={isSlideProject ? projectId : null}
              role="viewer"
              active={isPresentMode}
            />
          </div>,
          document.body,
        )}
    </DndContext>
  );
}

/**
 * Export dropdown button for slide projects
 */
function SlideExportButton({
  previewIframeRef,
  isExporting,
  projectId,
  getIdToken,
}: {
  previewIframeRef: React.RefObject<HTMLIFrameElement | null>;
  isExporting: boolean;
  projectId: string;
  getIdToken: () => Promise<string | null>;
}) {
  const [showMenu, setShowMenu] = useState(false);
  const slides = useSlideStore((s) => s.slides);
  const exportProgress = useSlideStore((s) => s.exportProgress);

  // Resume pending export job after page reload
  useEffect(() => {
    const pending = getPendingJob();
    if (pending && pending.projectId === projectId) {
      resumePendingExport(getIdToken);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExport = async (format: "pptx" | "pdf") => {
    setShowMenu(false);
    if (slides.length === 0 || !projectId) return;

    const store = useSlideStore.getState();
    store.setIsExporting(true);
    store.setExportProgress(0);

    try {
      if (format === "pptx") {
        await exportToPptx({
          title: "Presentation",
          slides,
          projectId,
          getIdToken,
        });
      } else {
        await exportToPdf({
          title: "Presentation",
          slides,
          projectId,
          getIdToken,
        });
      }
    } catch (err) {
      console.error(`[SlideExport] Failed to export as ${format}:`, err);
    } finally {
      store.setIsExporting(false);
      store.setExportProgress(0);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowMenu(!showMenu)}
        disabled={isExporting}
        className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded hover:bg-accent transition-colors text-muted-foreground hover:text-foreground disabled:opacity-50"
      >
        {isExporting ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <Download className="w-3.5 h-3.5" />
        )}
        {isExporting && exportProgress > 0 ? `${exportProgress}%` : "Export"}
      </button>

      {showMenu && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setShowMenu(false)}
          />
          <div className="absolute left-0 top-full mt-2 bg-popover border border-border rounded-lg shadow-lg py-1 z-50 min-w-[180px]">
            <button
              onClick={() => handleExport("pptx")}
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
            >
              Download as PPTX
            </button>
            <button
              onClick={() => handleExport("pdf")}
              className="w-full px-3 py-2 text-sm text-left hover:bg-accent transition-colors"
            >
              Download as PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}
